/**
 * BOGO Bundle Builder
 * State machine managing product selection, sticky bar, and cart integration.
 *
 * States: 0 items → 1 → 2 (checkout ready) → 3 → 4 (bonus checkout ready)
 * Each item tracks: productId, variantId, title, price, compareAtPrice, imageUrl, handle
 */

(function() {
  'use strict';

  const BOGO = {
    items: [],
    maxItems: 4,
    minCheckout: 2,
    bar: null,
    grid: null,
    overlay: null,
    quickView: null,
    isLoading: false,
    qvSwiper: null,

    init() {
      this.bar = document.querySelector('.bogo-bar');
      this.grid = document.querySelector('.bogo-grid');
      /* "pair" mode: any two pieces for one set price, priced through the bundle discount function.
         "bogo" (default) leaves pricing to the BOGO discount code, as before. */
      this.mode = (this.grid && this.grid.dataset.bogoMode) || 'bogo';
      this.pairCents = Math.round(parseFloat((this.grid && this.grid.dataset.pairPrice) || '0') * 100) || 0;
      this.MAX_DISCOUNT_RATE = 0.7; /* mirrors the function's ceiling; deeper pairs are not attempted */
      /* 3rd piece at a set price (0 = off). One set anchor covers the pair partner and this piece in the function. */
      this.thirdCents = Math.round(parseFloat((this.grid && this.grid.dataset.thirdPrice) || '0') * 100) || 0;
      if (this.mode === 'pair') this.maxItems = this.thirdCents ? 3 : 2;
      this.section = this.grid ? this.grid.closest('.bogo-builder-section') || document.body : document.body;
      this.replaceSlot = null;
      this.pendingThird = false;
      this.sheetEditUid = null;
      this.overlay = document.querySelector('.bogo-quick-view-overlay');
      this.quickView = document.querySelector('.bogo-quick-view');

      if (!this.bar || !this.grid) return;

      this.restoreState();
      this.bindEvents();
      this.markUnavailableVariants();
      this.render();
      this.moveSelectButtonsIntoCards();
      setTimeout(() => { this.defaultCardSizes(); this.renderGrid(); this.addCardGalleries(); }, 300);
      if (this.mode === 'pair') this.initFlow();
      this.resumeFromCart();

      // Show bar after a brief delay
      requestAnimationFrame(() => {
        this.bar.classList.add('bogo-bar--visible');
      });
    },

    // --- Sold-Out Variant Marking ---

    markUnavailableVariants() {
      const cards = this.grid.querySelectorAll('[data-bogo-product-id]');
      cards.forEach(card => {
        const productCard = card.querySelector('product-card');
        if (!productCard) return;

        const refEl = productCard.querySelector('[data-product-reference]');
        if (!refEl) return;

        const options = JSON.parse(refEl.dataset.options || '[]');
        const variantEls = productCard.querySelectorAll('[data-variant-reference]');

        // Build a map of option values to availability
        // For each option position, track which values have at least one available variant
        const optionAvailability = {};
        options.forEach((optName, idx) => {
          optionAvailability[optName] = {};
        });

        variantEls.forEach(vEl => {
          const vOptions = JSON.parse(vEl.dataset.options || '[]');
          const available = vEl.dataset.available === 'true';
          vOptions.forEach((val, idx) => {
            const optName = options[idx];
            if (optName) {
              if (!optionAvailability[optName][val]) {
                optionAvailability[optName][val] = false;
              }
              if (available) {
                optionAvailability[optName][val] = true;
              }
            }
          });
        });

        // Now mark selector buttons that have NO available variants
        const selectors = productCard.querySelectorAll('product-selector');
        selectors.forEach(selector => {
          const optName = selector.dataset.option;
          const buttons = selector.querySelectorAll('.selector__button');
          buttons.forEach(btn => {
            const value = btn.dataset.value;
            if (optionAvailability[optName] && optionAvailability[optName][value] === false) {
              btn.classList.add('bogo-unavailable');
            }
          });
        });

        // Also update the BOGO select button if the entire product is sold out
        const selectBtn = card.querySelector('.bogo-select-btn');
        if (selectBtn) {
          const anyAvailable = Array.from(variantEls).some(v => v.dataset.available === 'true');
          if (!anyAvailable && variantEls.length > 0) {
            selectBtn.disabled = true;
            selectBtn.textContent = 'Sold out';
            selectBtn.dataset.bogoSoldOut = 'true';
          }
        }
      });
    },

    // --- State Management ---

    addItem(item) {
      /* Pair mode: a full set can still swap a piece (replaceSlot), so capacity is canAddMore's call */
      if (this.mode !== 'pair' && this.items.length >= this.maxItems) return false;
      item.uid = item.uid || (Date.now().toString(36) + Math.random().toString(36).slice(2, 6));
      /* Pair mode: the same piece can go in twice (two chains, a chain in two lengths). The discount function
         treats the full-price copy as the anchor for the discounted one, so no function change is needed. */
      if (this.mode === 'pair') {
        if (this.replaceSlot != null && this.items[this.replaceSlot]) this.items[this.replaceSlot] = item;
        else if (this.pendingThird) this.items[2] = item;
        else this.items.push(item);
        this.replaceSlot = null;
        this.pendingThird = false;
        this.saveState();
        this.render();
        this.setView(this.items.length >= 2 ? 'summary' : 'pick');
        return true;
      }
      // Prevent duplicate product (same product, any variant)
      const existingIndex = this.items.findIndex(i => i.productId === item.productId);
      if (existingIndex !== -1) {
        // Update variant instead
        this.items[existingIndex] = item;
      } else {
        this.items.push(item);
      }
      this.saveState();
      this.render();
      return true;
    },

    removeItem(productId) {
      this.items = this.items.filter(i => i.productId !== productId);
      this.saveState();
      this.render();
    },

    /* Remove one picked piece (by its uid), leaving any other copy of the same piece in place */
    removeOne(uid) {
      const i = this.items.findIndex(x => x.uid === uid);
      if (i > -1 && this.items[i].inCartPairId) {
        try {
          const d = JSON.parse(sessionStorage.getItem('bogo_pair_dismissed') || '[]');
          d.push(this.items[i].inCartPairId);
          sessionStorage.setItem('bogo_pair_dismissed', JSON.stringify(d));
        } catch (err) { /* private mode: it may come back next visit, which is harmless */ }
      }
      if (i > -1) this.items.splice(i, 1);
      this.saveState();
      this.render();
    },

    /* Remove the most recent copy of a product */
    removeLastOf(productId) {
      for (let i = this.items.length - 1; i >= 0; i--) {
        if (this.items[i].productId === productId) { this.items.splice(i, 1); break; }
      }
      this.saveState();
      this.render();
    },

    updateItemVariant(productId, variantId, price, compareAtPrice, imageUrl) {
      const item = this.items.find(i => i.productId === productId);
      if (item) {
        item.variantId = variantId;
        item.price = price;
        item.compareAtPrice = compareAtPrice;
        if (imageUrl) item.imageUrl = imageUrl;
        this.saveState();
        this.render();
      }
    },

    isSelected(productId) {
      return this.items.some(i => i.productId === productId);
    },

    canAddMore() {
      if (this.mode === 'pair') return this.replaceSlot != null || (this.pendingThird && this.items.length === 2) || this.items.length < 2;
      return this.items.length < this.maxItems;
    },

    /* Full set (two pieces, not changing one): another piece opens as the 3rd piece at the set price, whether the
       shopper tapped its button, photo or title (Jay saw the old "BOGO slots full" dead end, Oct 11 2026) */
    primeThird(card) {
      if (this.mode === 'pair' && this.replaceSlot == null && !this.pendingThird && this.items.length === 2 && this.thirdCents && !this.isSelected(card.dataset.bogoProductId)) this.pendingThird = true;
    },

    canCheckout() {
      return this.items.length >= this.minCheckout;
    },

    saveState() {
      try {
        sessionStorage.setItem('bogo_items', JSON.stringify(this.items));
      } catch(e) { /* silent */ }
    },

    restoreState() {
      try {
        const saved = sessionStorage.getItem('bogo_items');
        if (saved) {
          this.items = JSON.parse(saved);
          // Validate items still exist in grid
          this.items = this.items.filter(item => {
            return this.grid.querySelector(`[data-bogo-product-id="${item.productId}"]`);
          });
          this.items.forEach(item => { if (!item.uid) item.uid = Date.now().toString(36) + Math.random().toString(36).slice(2, 6); });
        }
      } catch(e) {
        this.items = [];
      }
    },

    // --- Rendering ---

    render() {
      this.renderBar();
      this.renderGrid();
    },

    /* Cards land on their first variant (size 7 on a ring, 24" on some chains). In pair mode, start each
       card on the size most men buy, like the product pages do: rings 9 then 10, chains 20", bracelets
       the largest size. Only when that variant is in stock; otherwise the card keeps its own default. */
    /* Pair mode: put Add to pair inside the card, in the slot the card's own Add to cart used. */
    moveSelectButtonsIntoCards() {
      if (this.mode !== 'pair') return;
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(card => {
        this.sortSizeChips(card);
        const row = card.querySelector('.card-atc-row');
        const btn = card.querySelector('.bogo-select-btn');
        if (row && btn && btn.parentElement !== row) row.appendChild(btn);
      });
    },

    /* ===================== Set flow (pair mode) =====================
       Views on one page: landing (hero, proof, sets), pick (step 1 or 2 over the grid), summary (the set).
       The URL hash mirrors the view (#pick, #set) so the phone's back button steps back through it. */
    initFlow() {
      this.buildFilters();
      this.initHero();
      this.guardScrollLock();
      this.initLandingBar();
      /* Cards lead with the product shot (Jay, Oct 10 2026: comparing pieces reads cleaner); on-body stays the 2nd swipe.
         leadWithModel() is kept for an A/B test but not called. */
      this.section.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('[data-pb-start]')) { e.preventDefault(); this.replaceSlot = null; this.setView(this.items.length >= 2 ? 'summary' : 'pick'); return; }
        if (t.closest('[data-pb-back]')) { e.preventDefault(); this.setView('landing'); return; }
        if (t.closest('[data-pb-reset]')) { e.preventDefault(); this.startOver(); return; }
        const set = t.closest('[data-pb-set]');
        if (set) { e.preventDefault(); this.openSetQuickView(set); return; }
        const fin = t.closest('[data-pb-finish]');
        if (fin) { e.preventDefault(); this.applyFinish(fin.classList.contains('is-active') ? 'any' : fin.getAttribute('data-pb-finish')); return; }
        const f = t.closest('[data-pb-filter]');
        if (f) { e.preventDefault(); this.applyFilter(f.getAttribute('data-pb-filter')); return; }
        const ch = t.closest('[data-pb-change]');
        if (ch) { e.preventDefault(); this.replaceSlot = parseInt(ch.getAttribute('data-pb-change'), 10); this.setView('pick'); return; }
        const sz = t.closest('[data-pb-size]');
        if (sz) {
          e.preventDefault();
          const it = this.items.find(x => x.uid === sz.getAttribute('data-pb-size'));
          const card = it && this.grid.querySelector('[data-bogo-product-id="' + it.productId + '"]');
          if (card) { this.setCardVariant(card, it.variantId); this.sheetEditUid = it.uid; this.openSizeSheet(card); }
          return;
        }
        const rm = t.closest('[data-pb-remove]');
        if (rm) { e.preventDefault(); this.removeOne(rm.getAttribute('data-pb-remove')); return; }
        const sc = t.closest('[data-pb-scroll]');
        if (sc) {
          e.preventDefault();
          const row = sc.parentElement.querySelector('.pb-third__row');
          /* Animate by hand: smooth scrollBy is ignored in some browsers, which left the arrows dead */
          if (row) {
            const from = row.scrollLeft;
            const to = Math.max(0, Math.min(row.scrollWidth - row.clientWidth, from + (sc.getAttribute('data-pb-scroll') === 'prev' ? -1 : 1) * Math.max(140, row.clientWidth - 60)));
            const t0 = performance.now();
            const step = now => { const k = Math.min(1, (now - t0) / 260); row.scrollLeft = from + (to - from) * (1 - Math.pow(1 - k, 3)); if (k < 1) requestAnimationFrame(step); };
            requestAnimationFrame(step);
          }
          return;
        }
        const th = t.closest('[data-pb-third]');
        if (th) {
          e.preventDefault();
          const card = this.grid.querySelector('[data-bogo-product-id="' + th.getAttribute('data-pb-third') + '"]');
          if (!card) return;
          this.pendingThird = true;
          this.openQuickView(card);
        }
      });
      /* 3rd-piece row: mouse drag scrolls it (touch and trackpads already do); a drag is not a tap */
      let drag = null;
      this.section.addEventListener('pointerdown', (e) => {
        const row = e.target.closest('[data-pb-drag]');
        if (!row || e.pointerType !== 'mouse' || e.button !== 0) return;
        drag = { row: row, x: e.clientX, left: row.scrollLeft, moved: false };
      });
      window.addEventListener('pointermove', (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        if (Math.abs(dx) > 4) { drag.moved = true; drag.row.classList.add('is-dragging'); }
        if (drag.moved) { drag.row.scrollLeft = drag.left - dx; e.preventDefault(); }
      });
      window.addEventListener('pointerup', () => {
        if (!drag) return;
        const d = drag; drag = null;
        d.row.classList.remove('is-dragging');
        if (d.moved) { const block = ev => { ev.stopPropagation(); ev.preventDefault(); }; d.row.addEventListener('click', block, { capture: true, once: true }); setTimeout(() => d.row.removeEventListener('click', block, { capture: true }), 50); }
      });
      /* Set cards are role=button: Enter or Space opens them like a tap */
      this.section.addEventListener('keydown', (e) => {
        const set = e.target.closest && e.target.closest('[data-pb-set]');
        if (set && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); this.openSetQuickView(set); }
      });
      /* Set quick view: tap a size chip */
      if (this.quickView) this.quickView.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-pb-qvset-chip]');
        if (!chip || !this.qvSet) return;
        const [pid, vid] = chip.getAttribute('data-pb-qvset-chip').split('|');
        this.qvSet.picks[pid] = vid;
        this.renderSetQuickView(false);
      });
      /* Set quick view: a size per piece */
      if (this.quickView) this.quickView.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-pb-qvset-size]');
        if (!sel || !this.qvSet) return;
        this.qvSet.picks[sel.getAttribute('data-pb-qvset-size')] = sel.value;
        this.renderSetQuickView(false);
      });
      /* Summary size dropdown: move that pick to the chosen size, keep its place in the set */
      this.section.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-pb-size-select]');
        if (!sel) return;
        const uid = sel.getAttribute('data-pb-size-select');
        const it = this.items.find(x => x.uid === uid);
        const card = it && this.grid.querySelector('[data-bogo-product-id="' + it.productId + '"]');
        if (!card) return;
        this.setCardVariant(card, sel.value);
        setTimeout(() => {
          const fresh = this.itemFromCard(card);
          const i = this.items.findIndex(x => x.uid === uid);
          if (fresh && i > -1 && String(fresh.variantId) === String(sel.value)) { fresh.uid = uid; this.items[i] = fresh; this.saveState(); this.render(); }
        }, 120);
      });
      this.bar.addEventListener('click', (e) => {
        const g = e.target.closest('[data-pb-goto]');
        if (!g) return;
        const step = parseInt(g.getAttribute('data-pb-goto'), 10);
        if (step === 3) { if (this.items.length >= 2) this.setView('summary'); return; }
        this.replaceSlot = this.items[step - 1] ? step - 1 : null;
        this.setView('pick');
      });
      /* The builder places the page itself on every step change; the browser restoring old scroll positions on Back
         would throw the shopper to the top of the grid */
      try { history.scrollRestoration = 'manual'; } catch (err) {}
      window.addEventListener('popstate', () => {
        /* Our own history.back() after closing the photo viewer with the X */
        if (this.zoomPopSkip) { this.zoomPopSkip = false; return; }
        if (this.zoomOpen) { this.closeZoom(true); return; }
        /* Back with a quick view open closes the quick view and keeps the step you were on (QA, Oct 11 2026) */
        if (this.quickView && this.quickView.classList.contains('bogo-qv-open')) {
          this.closeQuickView();
          const url = location.pathname + location.search + (this.view === 'pick' ? '#pick' : this.view === 'summary' ? '#set' : '');
          history.pushState({ pb: this.view }, '', url);
          return;
        }
        this.setView(this.viewFromHash(), { push: false });
      });
      const moreFromHash = () => { const m = (location.hash || '').match(/^#more=([\w-]+)/); return m ? m[1] : null; };
      window.addEventListener('hashchange', () => { const pid = moreFromHash(); if (pid) this.openFromCart(pid); });
      if (moreFromHash()) { this.openFromCart(moreFromHash()); return; }
      const start = this.viewFromHash();
      this.setView(start === 'landing' && this.items.length ? (this.items.length >= 2 ? 'summary' : 'pick') : start, { push: false, scroll: false });
    },

    /* Hero: swipeable lifestyle photos with a progress line. Swiper loads deferred from the CDN, so wait for it. */
    initHero(attempt) {
      const el = this.section.querySelector('[data-pb-hero]');
      if (!el || el.swiper) return;
      if (typeof window.Swiper === 'undefined') {
        if ((attempt || 0) < 40) setTimeout(() => this.initHero((attempt || 0) + 1), 100);
        return;
      }
      if (el.querySelectorAll('.swiper-slide').length < 2) return;
      new window.Swiper(el, {
        slidesPerView: 1,
        speed: 350,
        pagination: { el: el.querySelector('.pb-hero__progress'), type: 'progressbar' }
      });
    },

    /* On-body photo first on phones: move each card's model slides ahead of its product shot, then reset the slider */
    leadWithModel() {
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(card => {
        const track = card.querySelector('product-slider .slider-slides');
        if (!track) return;
        const models = Array.from(track.querySelectorAll(':scope > [data-model-image]'));
        const first = track.querySelector(':scope > [data-featured-image]');
        if (!models.length || !first) return;
        models.forEach(m => track.insertBefore(m, first));
        const ps = card.querySelector('product-slider');
        if (ps && ps.carousel) { ps.carousel.update(); ps.carousel.slideTo(0, 0); }
      });
    },

    /* "Add a 3rd piece for $39" from the cart drawer lands on #more=<set id>: load that set from the cart into
       the builder summary; adding replaces the cart set with the 3-piece one */
    async openFromCart(pid) {
      let cart;
      try { cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()); } catch (err) { return false; }
      const lines = cart.items.filter(l => l.properties && l.properties._pair_id === pid);
      if (lines.length < 2) return false;
      lines.sort((a, b) => (parseInt(a.properties._set_piece, 10) || 9) - (parseInt(b.properties._set_piece, 10) || 9));
      this.items = lines.filter(l => this.grid.querySelector('[data-bogo-product-id="' + l.product_id + '"]')).slice(0, 2).map(l => ({
        productId: String(l.product_id), variantId: String(l.variant_id), title: l.product_title, price: this.money(l.original_price),
        priceCents: l.original_price, compareAtPrice: '', imageUrl: l.image || '', handle: l.handle,
        uid: 'cart-' + l.key, inCartPairId: pid
      }));
      if (this.items.length < 2) { this.items = []; return false; }
      this.saveState();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') drawer.close();
      history.replaceState(null, '', location.pathname + location.search + '#set');
      this.setView('summary', { push: false });
      return true;
    },

    startOver() {
      this.items = [];
      this.replaceSlot = null;
      this.pendingThird = false;
      this.saveState();
      this.setView('landing');
    },

    viewFromHash() {
      const h = (location.hash || '').replace('#', '');
      if (h === 'set') return this.items.length >= 2 ? 'summary' : 'pick';
      if (h === 'pick') return 'pick';
      return 'landing';
    },

    setView(view, opts) {
      opts = opts || {};
      if (view === 'summary' && this.items.length < 2) view = 'pick';
      if (view !== 'pick') this.replaceSlot = null;
      this.view = view;
      this.section.setAttribute('data-pb-view', view);
      if (opts.push !== false) {
        const url = location.pathname + location.search + (view === 'pick' ? '#pick' : view === 'summary' ? '#set' : '');
        if (url !== location.pathname + location.search + location.hash) history.pushState({ pb: view }, '', url);
      }
      if (view === 'landing') {
        /* Landing shows the whole catalog: no filter, no step-2 ordering */
        this.applyFilter('all');
        this.applyFinish('any', true);
        this.grid.querySelectorAll('[data-bogo-product-id]').forEach(c => { c.style.order = ''; c.classList.remove('pb-is-match'); const t = c.querySelector('.pb-match-tag'); if (t) t.remove(); });
      }
      if (view === 'pick') {
        const slot = this.replaceSlot != null ? this.replaceSlot : Math.min(this.items.length, 1);
        const nth = slot === 0 ? '1st' : '2nd';
        this.section.querySelector('[data-pb-step-title]').textContent = (this.replaceSlot != null ? 'Change your ' : 'Pick your ') + nth + ' piece';
        this.section.querySelector('[data-pb-step-count]').textContent = (slot + 1) + ' / 2';
        this.applyFilter('all');
        this.applyMatch(slot);
        this.matchFinish(slot);
        /* A finish picked on step 1 carries to step 2 */
        if (this.finishFilter && this.finishFilter !== 'any') this.applyFinish(this.finishFilter);
      }
      this.render();
      if (opts.scroll !== false) {
        /* Pick and summary are the whole page (everything above the builder is hidden), so the step title is at the
           very top. (Measuring the header broke once it slid off screen: its bottom went negative and the page landed
           ~570px past the title after the first pick.) */
        const top = 0;
        window.scrollTo({ top: top, behavior: 'auto' });
      }
    },

    /* Type chips (All, Bracelets, Chains, Rings) built from the cards, with counts */
    buildFilters() {
      const wrap = this.section.querySelector('[data-pb-filters]');
      if (!wrap) return;
      const counts = {};
      const cards = Array.from(this.grid.querySelectorAll('[data-bogo-product-id]'));
      cards.forEach(c => { const t = c.dataset.bogoType || 'Other'; counts[t] = (counts[t] || 0) + 1; });
      const order = ['Chain', 'Bracelet', 'Ring'];
      const types = Object.keys(counts).sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
      wrap.innerHTML = '<button type="button" class="pb-cat is-active" data-pb-filter="all">All</button>' +
        types.map(t => '<button type="button" class="pb-cat" data-pb-filter="' + t.replace(/"/g, '') + '">' + t + 's</button>').join('');
    },

    /* Finish chips: every card switches to that finish (in stock, most-bought size); cards without it hide */
    applyFinish(fam, quiet) {
      this.finishFilter = fam;
      this.section.querySelectorAll('[data-pb-finish]').forEach(b => b.classList.toggle('is-active', b.getAttribute('data-pb-finish') === fam));
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(c => {
        const refEl = c.querySelector('[data-product-reference]');
        if (fam === 'any') {
          c.classList.remove('pb-hidden-fin');
          /* Tapping the active pill again clears it and puts each card back on the finish it showed before */
          if (!quiet && c.dataset.pbPreFinish && refEl && refEl.dataset.selected !== c.dataset.pbPreFinish) this.setCardVariant(c, c.dataset.pbPreFinish);
          delete c.dataset.pbPreFinish;
          return;
        }
        if (!c.dataset.pbPreFinish && refEl) c.dataset.pbPreFinish = refEl.dataset.selected || '';
        const want = this.variantInFamily(c, fam);
        c.classList.toggle('pb-hidden-fin', !want);
        if (want && !quiet) this.setCardVariant(c, want);
      });
      this.paintEmpty();
    },

    applyFilter(type) {
      this.section.querySelectorAll('[data-pb-filter]').forEach(b => b.classList.toggle('is-active', b.getAttribute('data-pb-filter') === type));
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(c => {
        c.classList.toggle('pb-hidden', type !== 'all' && (c.dataset.bogoType || 'Other') !== type);
      });
      this.paintEmpty();
    },

    /* Type + finish can leave nothing (e.g. Bracelets in Black Titanium): say so instead of a blank grid */
    paintEmpty() {
      if (this.mode !== 'pair' || !this.grid) return;
      const any = Array.from(this.grid.querySelectorAll('[data-bogo-product-id]')).some(c => !c.classList.contains('pb-hidden') && !c.classList.contains('pb-hidden-fin'));
      let note = this.section.querySelector('.pb-empty');
      if (!note) {
        note = document.createElement('div');
        note.className = 'pb-empty';
        note.innerHTML = '<p>Nothing here in this finish yet.</p><button type="button" class="pb-empty__reset">Show all pieces</button>';
        this.grid.insertAdjacentElement('beforebegin', note);
        note.querySelector('button').addEventListener('click', () => { this.applyFilter('all'); this.applyFinish('any'); });
      }
      note.hidden = any;
    },

    /* Step 2: the matched partner of the first pick goes first (matched sets sell ~8x mismatched ones) */
    applyMatch(slot) {
      const note = this.section.querySelector('[data-pb-match]');
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(c => { c.style.order = ''; c.classList.remove('pb-is-match'); const t = c.querySelector('.pb-match-tag'); if (t) t.remove(); });
      /* Pieces already in the set sit at the end, so step 2 does not open on the piece you just picked */
      this.items.forEach((it, i) => {
        if (i === slot) return;
        const c = this.grid.querySelector('[data-bogo-product-id="' + it.productId + '"]');
        if (c) c.style.order = '999';
      });
      if (note) note.hidden = true;
      const other = this.items[slot === 0 ? 1 : 0];
      if (!other) return;
      const otherCard = this.grid.querySelector('[data-bogo-product-id="' + other.productId + '"]');
      const otherMatch = otherCard ? otherCard.dataset.bogoMatch : '';
      let n = 0;
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(c => {
        if (c.dataset.bogoProductId === other.productId) return;
        if (c.dataset.bogoMatch === other.productId || (otherMatch && c.dataset.bogoProductId === otherMatch)) {
          c.style.order = '-1'; c.classList.add('pb-is-match'); n++;
          const media = c.querySelector('product-slider .carousel-wrapper') || c.querySelector('product-slider .slider');
          if (media && !media.querySelector('.pb-match-tag')) { const t = document.createElement('span'); t.className = 'pb-match-tag'; t.textContent = 'Made to match'; media.appendChild(t); }
        }
      });
      if (note) {
        const inBag = other.inCartPairId ? 'Your ' + other.title + ' is already in your bag. ' : '';
        note.textContent = inBag.trim();
        note.hidden = !note.textContent;
      }
    },

    /* Step 2 (or changing a piece): every card starts in the other piece's finish where it has one in stock, so the
       set matches by default (repeat buyers stay in one metal ~79% of the time) */
    matchFinish(slot) {
      const other = this.items[slot === 0 ? 1 : 0];
      if (!other) return;
      const otherCard = this.grid.querySelector('[data-bogo-product-id="' + other.productId + '"]');
      const ref = otherCard && otherCard.querySelector('[data-variant-reference][data-id="' + other.variantId + '"]');
      let names = [], opts = [];
      try { names = JSON.parse(otherCard.querySelector('[data-product-reference]').dataset.options || '[]'); opts = JSON.parse(ref.getAttribute('data-options') || '[]'); } catch (err) { return; }
      const fi = names.findIndex(n => /finish|color|metal/i.test(n));
      const family = this.finishFamily(fi > -1 ? opts[fi] : '');
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(card => {
        if (card.dataset.bogoProductId === other.productId) return;
        const curRef = card.querySelector('[data-product-reference]');
        const cur = curRef && card.querySelector('[data-variant-reference][data-id="' + curRef.dataset.selected + '"]');
        let curOpts = [], cardNames = [];
        try { cardNames = JSON.parse(curRef.dataset.options || '[]'); curOpts = JSON.parse(cur.getAttribute('data-options') || '[]'); } catch (err) {}
        const cfi = cardNames.findIndex(n => /finish|color|metal/i.test(n));
        if (cfi > -1 && this.finishFamily(curOpts[cfi]) === family && this.isCurrentVariantAvailable(card)) return;
        const want = this.variantInFamily(card, family);
        if (want) this.setCardVariant(card, want);
      });
    },

    /* Put a card on a given variant by clicking its own option buttons */
    setCardVariant(card, variantId) {
      const ref = card.querySelector('[data-variant-reference][data-id="' + variantId + '"]');
      if (!ref) return;
      let opts = [];
      try { opts = JSON.parse(ref.getAttribute('data-options') || '[]'); } catch (err) { return; }
      card.querySelectorAll('product-selector').forEach(ps => {
        ps.querySelectorAll('.selector__button[data-value]').forEach(b => {
          if (opts.indexOf(b.getAttribute('data-value')) > -1 && !b.classList.contains('selected-value')) b.click();
        });
      });
    },

    /* Summary: finish as text, size as a grey dropdown (sold-out sizes in this finish disabled) */
    variantWithSize(item, sized) {
      const esc = v => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      const card = this.grid.querySelector('[data-bogo-product-id="' + item.productId + '"]');
      if (!sized || !card) return esc(this.variantLabel(item));
      let names = [];
      try { names = JSON.parse(card.querySelector('[data-product-reference]').dataset.options || '[]'); } catch (err) { names = []; }
      const si = names.findIndex(n => /size/i.test(n));
      const refs = Array.from(card.querySelectorAll('[data-variant-reference][data-options]')).map(r => { let o = []; try { o = JSON.parse(r.getAttribute('data-options')); } catch (err) {} return { id: r.getAttribute('data-id'), opts: o, ok: r.getAttribute('data-available') === 'true' }; });
      const cur = refs.find(r => r.id === String(item.variantId));
      if (si < 0 || !cur) return esc(this.variantLabel(item));
      const same = r => r.opts.every((v, k) => k === si || v === cur.opts[k]);
      const sizes = refs.filter(same).sort((a, b) => parseFloat(String(a.opts[si]).replace('#', '')) - parseFloat(String(b.opts[si]).replace('#', '')));
      const rest = cur.opts.filter((v, k) => k !== si).join(' / ');
      const sel = '<span class="pb-row__size"><select data-pb-size-select="' + esc(item.uid) + '" aria-label="Size">' +
        sizes.map(r => '<option value="' + esc(r.id) + '"' + (r.id === cur.id ? ' selected' : '') + (r.ok || r.id === cur.id ? '' : ' disabled') + '>Size ' + esc(r.opts[si]) + (r.ok ? '' : ' (sold out)') + '</option>').join('') +
        '</select></span>';
      return (rest ? esc(rest) + ' / ' : '') + sel;
    },

    variantLabel(item) {
      const ref = this.grid.querySelector('[data-bogo-product-id="' + item.productId + '"] [data-variant-reference][data-id="' + item.variantId + '"]');
      if (!ref) return '';
      try { return JSON.parse(ref.getAttribute('data-options') || '[]').join(' / '); } catch (err) { return ''; }
    },

    /* Finish family of a variant: gold, black or steel (silver and plain steel count as steel) */
    finishFamily(value) {
      const v = String(value || '').toLowerCase();
      if (v.indexOf('gold') > -1) return 'gold';
      if (v.indexOf('black') > -1) return 'black';
      return 'steel';
    },

    /* The card's in-stock variant in a finish family, at the size most men buy when it is in stock */
    variantInFamily(card, family) {
      const refEl = card.querySelector('[data-product-reference]');
      let names = [];
      try { names = JSON.parse((refEl && refEl.dataset.options) || '[]'); } catch (err) { names = []; }
      const si = names.findIndex(n => /size/i.test(n));
      const fi = names.findIndex(n => /finish|color|metal/i.test(n));
      const refs = Array.from(card.querySelectorAll('[data-variant-reference][data-options]')).map(r => { let o = []; try { o = JSON.parse(r.getAttribute('data-options')); } catch (err) {} return { id: r.getAttribute('data-id'), opts: o, ok: r.getAttribute('data-available') === 'true' }; });
      /* No finish option (e.g. the Perk necklaces, one product per finish): read the finish from the title */
      if (fi < 0 && this.finishFamily(card.dataset.bogoProductTitle || '') !== family) return null;
      /* Single-variant products (Perk necklaces) carry no option-tagged variant refs: use the card's own variant */
      if (fi < 0 && !refs.length) return (refEl && refEl.dataset.selected) || (card.querySelector('[data-variant-reference][data-id]') || {}).dataset?.id || null;
      const pool = refs.filter(r => r.ok && (fi < 0 || this.finishFamily(r.opts[fi]) === family));
      if (!pool.length) return null;
      if (si > -1) {
        const norm = v => String(v).replace('#', '').replace('"', '').trim();
        const wants = this.preferredSizes(refs.map(r => r.opts[si]));
        for (const w of wants) { const hit = pool.find(r => norm(r.opts[si]) === w); if (hit) return hit.id; }
      }
      return pool[0].id;
    },

    /* Best-selling set quick view: set photo + each piece, one finish for both, a size per piece; Add to set loads it */
    openSetQuickView(el) {
      if (!this.quickView || !this.overlay) return;
      const ids = el.getAttribute('data-pb-set').split(',');
      const cards = ids.map(id => this.grid.querySelector('[data-bogo-product-id="' + id + '"]')).filter(Boolean);
      if (cards.length < 2) return;
      const families = ['steel', 'gold', 'black'].filter(f => cards.every(c => this.variantInFamily(c, f)));
      if (!families.length) return;
      this.qvSet = { cards: cards, title: el.getAttribute('data-pb-set-title') || '', img: el.getAttribute('data-pb-set-img') || '', imgsBy: ['steel', 'gold', 'black'].reduce((o, f) => { o[f] = (el.getAttribute('data-pb-set-imgs-' + f) || '').split('|').filter(Boolean); return o; }, {}), families: families, family: families[0], picks: {} };
      cards.forEach(c => { this.qvSet.picks[c.dataset.bogoProductId] = this.variantInFamily(c, families[0]); });
      this.quickView.dataset.bogoProductId = '';
      this.renderSetQuickView(true);
      /* Every quick view opens at the top: the scroll box kept the last product's position (Jay, Oct 11 2026) */
      const qvScroll = this.quickView.querySelector('.bogo-qv__scroll');
      if (qvScroll) qvScroll.scrollTop = 0;
      this.overlay.classList.add('bogo-qv-open');
      this.quickView.classList.add('bogo-qv-open');
      document.body.style.overflow = 'hidden';
    },

    /* First screen: show the set bar once Start building has scrolled off the top; tapping its total row starts picking */
    initLandingBar() {
      const start = this.section.querySelector('[data-pb-start]');
      if (!start || !this.bar) return;
      const toggle = () => {
        const past = start.getBoundingClientRect().bottom < 0;
        this.section.classList.toggle('pb-bar-peek', past);
      };
      window.addEventListener('scroll', toggle, { passive: true });
      window.addEventListener('resize', toggle);
      toggle();
      this.bar.addEventListener('click', (e) => {
        if (this.view !== 'landing' || e.target.closest('button')) return;
        if (e.target.closest('.pb-bar__row')) { this.replaceSlot = null; this.setView(this.items.length >= 2 ? 'summary' : 'pick'); }
      });
    },

    /* Popups (Alia, Postscript) are hidden on this page with CSS, but Alia still opens its popup unseen and locks the
       page with body overflow:hidden, so nobody can scroll (Jay, Oct 11 2026). Undo any inline lock that isn't ours:
       the quick view and the size guide modal are the only things here that lock the body inline. */
    guardScrollLock() {
      const body = document.body;
      const ours = () => document.querySelector('.bogo-qv-open, .modal--size-guide__wrapper.is-active, .modal--size-guide__wrapper.is-shown');
      const check = () => {
        if (ours()) return;
        if (body.style.overflow === 'hidden' || body.style.overflowY === 'hidden') { body.style.removeProperty('overflow'); body.style.removeProperty('overflow-y'); }
        if (document.documentElement.style.overflow === 'hidden') document.documentElement.style.removeProperty('overflow');
      };
      new MutationObserver(check).observe(body, { attributes: true, attributeFilter: ['style'] });
      new MutationObserver(check).observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
      check();
    },

    setVariantRefs(card) {
      const refEl = card.querySelector('[data-product-reference]');
      let names = [];
      try { names = JSON.parse((refEl && refEl.dataset.options) || '[]'); } catch (err) { names = []; }
      const si = names.findIndex(n => /size/i.test(n));
      const fi = names.findIndex(n => /finish|color|metal/i.test(n));
      const refs = Array.from(card.querySelectorAll('[data-variant-reference][data-options]')).map(r => {
        let o = []; try { o = JSON.parse(r.getAttribute('data-options')); } catch (err) {}
        const pm = r.querySelector('.variant-price-markup');
        return { id: r.getAttribute('data-id'), opts: o, ok: r.getAttribute('data-available') === 'true', cents: (pm && this.toCents(pm.textContent)) || parseInt(card.dataset.bogoPriceCents, 10) || 0 };
      });
      return { si: si, fi: fi, refs: refs };
    },

    /* A card's own swatch image for a finish family (same files the product cards and single quick view use) */
    swatchFor(cards, family) {
      for (const c of cards) {
        const b = Array.from(c.querySelectorAll('.selector__button--type-swatch[data-value]')).find(x => this.finishFamily(x.getAttribute('data-value')) === family);
        const img = b && b.querySelector('img');
        if (img) return img.getAttribute('src');
      }
      return '';
    },

    renderSetQuickView(withGallery) {
      const q = this.qvSet; if (!q) return;
      this.togglePairQvAnother(false);
      const esc = v => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      const label = { steel: 'Polished steel', gold: '18K gold', black: 'Black' };
      let list = 0;
      const pieces = q.cards.map(card => {
        const v = this.setVariantRefs(card);
        const pick = q.picks[card.dataset.bogoProductId];
        const cur = v.refs.find(r => r.id === pick);
        if (cur) list += cur.cents;
        const fam = v.refs.filter(r => v.fi < 0 || this.finishFamily(r.opts[v.fi]) === q.family);
        /* Same round size chips as the single-piece quick view (sold-out sizes greyed and struck, not tappable) */
        const curSize = v.si > -1 && cur ? cur.opts[v.si] : '';
        let chips = '';
        if (v.si > -1 && fam.length) {
          const sorted = fam.slice().sort((a, b) => parseFloat(String(a.opts[v.si]).replace('#', '')) - parseFloat(String(b.opts[v.si]).replace('#', '')));
          chips = '<div class="pb-qvset__chips">' + sorted.map(r => '<button type="button" class="pb-qvset__chip' + (r.id === pick ? ' is-on' : '') + (r.ok ? '' : ' is-out') + '"' + (r.ok ? ' data-pb-qvset-chip="' + card.dataset.bogoProductId + '|' + r.id + '"' : ' disabled') + ' aria-label="' + esc(card.dataset.bogoProductTitle) + ' size ' + esc(r.opts[v.si]) + (r.ok ? '' : ', sold out') + '">' + esc(r.opts[v.si]) + '</button>').join('') + '</div>';
        }
        return '<div class="pb-qvset__piece"><p class="pb-qvset__label">' + esc(card.dataset.bogoProductTitle) + (curSize ? ': <span>' + esc(curSize) + '</span>' : '') + '</p>' + chips + '</div>';
      }).join('');
      const save = list - this.pairCents;
      this.quickView.querySelector('.bogo-qv__title').textContent = q.title;
      this.quickView.querySelector('.bogo-qv__price').innerHTML = '<strong class="pb-qv-price">' + this.money(this.pairCents) + '</strong> <s>' + this.money(list) + '</s>' + (save > 0 ? '<span class="pb-qv-save">Save ' + this.money(save) + '</span>' : '');
      const desc = this.quickView.querySelector('.bogo-qv__description');
      if (desc) { desc.innerHTML = ''; desc.style.display = 'none'; } /* no filler copy on set quick views (Jay, Oct 11 2026) */
      this.quickView.querySelector('.bogo-qv__selectors').innerHTML =
        /* Finish as the same round swatches as the single-piece quick view, using the cards' own swatch images */
        '<p class="pb-qvset__label">Finish: <span>' + label[q.family] + '</span></p><div class="pb-qvset__swatches">' + q.families.map(f => '<button type="button" class="pb-qvset__swatch' + (f === q.family ? ' is-on' : '') + '" data-pb-qvset-family="' + f + '" aria-label="' + label[f] + '">' + (this.swatchFor(q.cards, f) ? '<img src="' + esc(this.swatchFor(q.cards, f)) + '" alt="">' : '<i class="pb-qvset__dot pb-qvset__dot--' + f + '"></i>') + '</button>').join('') + '</div>' +
        '<div class="pb-qvset__pieces">' + pieces + '</div>';
      const btn = this.quickView.querySelector('.bogo-qv__select-btn');
      btn.textContent = 'Add set to cart'; btn.disabled = false; btn.classList.remove('bogo-qv__remove-btn');
      if (withGallery !== false) {
        const slides = [];
        /* The set's own photos for the chosen finish; a finish with no set product shows just its pieces, never another finish's set photo */
        const setImgs = (q.imgsBy && q.imgsBy[q.family]) || [];
        if (setImgs.length) setImgs.forEach(src => slides.push({ src: src, alt: q.title, variantIds: [], type: 'featured' }));
        else if (q.img && q.family === 'steel' && !(q.imgsBy && Object.values(q.imgsBy).some(a => a.length))) slides.push({ src: q.img, alt: q.title, variantIds: [], type: 'featured' });
        q.cards.forEach(card => {
          this.qvSourceCard = card;
          const s1 = this.buildSlidesForVariant(this.readCardImages(card, card.dataset.bogoProductTitle), q.picks[card.dataset.bogoProductId]);
          if (s1[0]) slides.push(s1[0]);
        });
        this.qvSourceCard = null;
        this.renderQvGallery(slides);
      }
    },

    /* Add the set from its quick view: put each card on the chosen variant, then the summary */
    addSetFromQuickView() {
      const q = this.qvSet; if (!q) return;
      const picks = [];
      q.cards.forEach(card => {
        const id = q.picks[card.dataset.bogoProductId];
        if (id) this.setCardVariant(card, id);
        const it = this.itemFromCard(card);
        if (it && this.isCurrentVariantAvailable(card)) { it.uid = Date.now().toString(36) + Math.random().toString(36).slice(2, 6); picks.push(it); }
      });
      if (picks.length < 2) return;
      /* Sets go straight to the cart (the 3rd piece is offered in the drawer); keeps a set to 3 taps */
      this.items = picks;
      this.saveState();
      this.closeQuickView();
      this.addToCart();
    },

    /* Best-selling set: both pieces in one finish (steel, the set photo's finish, unless a piece has none in stock),
       at the most common in-stock size, then the summary */
    loadSet(ids) {
      const picks = [];
      const cards = ids.map(id => this.grid.querySelector('[data-bogo-product-id="' + id + '"]')).filter(Boolean);
      const family = ['steel', 'gold', 'black'].find(f => cards.every(c => this.variantInFamily(c, f))) || null;
      cards.forEach(card => {
        const id = card.dataset.bogoProductId;
        const want = family && this.variantInFamily(card, family);
        if (want) this.setCardVariant(card, want);
        if (!this.isCurrentVariantAvailable(card)) {
          const open = this.cardSizeChips(card).find(b => this.sizeInStock(card, b.getAttribute('data-value')));
          if (open) open.click();
        }
        const it = this.itemFromCard(card);
        if (it && this.isCurrentVariantAvailable(card)) { it.uid = Date.now().toString(36) + Math.random().toString(36).slice(2, 6); picks.push(it); }
      });
      if (picks.length < 2) return;
      this.items = picks;
      this.saveState();
      this.setView('summary');
    },

    renderPairBar() {
      const esc = v => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      /* Keep each pick's photo on its own finish */
      this.items.forEach(it => {
        const u = this.imageForVariant(this.grid.querySelector('[data-bogo-product-id="' + it.productId + '"]'), it.variantId);
        if (u) it.imageUrl = u;
      });
      /* What's in the set so far, above the grid: thumb, slot, name; tap to change that piece */
      const picked = this.section.querySelector('[data-pb-picked]');
      if (picked) {
        const names = ['1st piece', '2nd piece', '3rd piece'];
        picked.innerHTML = this.items.slice(0, 2).map((it, i) =>
          '<button type="button" class="pb-picked__item' + (this.replaceSlot === i ? ' is-changing' : '') + '" data-pb-change="' + i + '">' +
          '<span class="pb-picked__img">' + (it.imageUrl ? '<img src="' + esc(it.imageUrl) + '" alt="">' : '') + '</span>' +
          '<span class="pb-picked__text"><span class="pb-picked__slot">' + names[i] + (this.replaceSlot === i ? ', changing' : '') + '</span><span class="pb-picked__name">' + esc(it.title) + '</span></span></button>').join('');
        picked.hidden = !this.items.length;
      }
      this.bar.querySelectorAll('[data-pb-steps] li').forEach(li => {
        const k = parseInt(li.getAttribute('data-step'), 10);
        const b = li.querySelector('button');
        const it = k < 3 ? this.items[k - 1] : null;
        const label = k === 1 ? '1st piece' : k === 2 ? '2nd piece' : 'Your set';
        b.innerHTML = (it && it.imageUrl ? '<img src="' + esc(it.imageUrl) + '" alt="">' : '') + '<span>' + label + '</span>';
      });
      const plan = this.pairPlan();
      const n = this.items.length;
      /* Running total: $0, then the first piece's price, then the set price once two are in */
      const total = n >= 2 ? plan.payCents : n === 1 ? (this.items[0].priceCents || this.toCents(this.items[0].price)) : 0;
      const save = n >= 2 ? plan.listCents - plan.payCents : 0;
      const q = sel => this.bar.querySelector(sel);
      q('[data-pb-total]').textContent = this.money(total);
      q('[data-pb-was]').textContent = save > 0 ? this.money(plan.listCents) : '';
      /* Goal nudge while the set is incomplete: the running total alone reads like a price per piece */
      const pairTxt = this.money(this.pairCents || 9900);
      q('[data-pb-save]').textContent = save > 0 ? 'You save ' + this.money(save) : '';
      const nudge = q('[data-pb-nudge]');
      if (nudge && !(this.cartErrorUntil > Date.now())) { nudge.textContent = n === 1 && this.replaceSlot == null ? 'Add 1 more: get both for ' + pairTxt : ''; nudge.hidden = !nudge.textContent; }
      const step = this.view === 'summary' ? 3 : (this.replaceSlot != null ? this.replaceSlot + 1 : Math.min(n + 1, 2));
      this.bar.querySelectorAll('[data-pb-steps] li').forEach(li => {
        const k = parseInt(li.getAttribute('data-step'), 10);
        li.classList.toggle('is-done', k < 3 ? !!this.items[k - 1] : n >= 2);
        li.classList.toggle('is-current', k === step);
      });
      const cta = q('.bogo-bar__cta-btn');
      this.bar.classList.toggle('is-incomplete', n < 2 && !this.justAdded);
      if (this.justAdded) { cta.disabled = true; q('.bogo-bar__cta-text').textContent = 'Added to cart \u2713'; return; }
      cta.disabled = n < 2;
      q('.bogo-bar__cta-text').textContent = 'Add set to cart';
    },

    renderSummary() {
      const box = this.section.querySelector('[data-pb-summary]');
      if (!box) return;
      const plan = this.pairPlan();
      const names = ['1st piece', '2nd piece', '3rd piece'];
      const esc = v => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      const rows = this.items.map((it, i) => {
        const card = this.grid.querySelector('[data-bogo-product-id="' + it.productId + '"]');
        const sized = card && this.cardSizeChips(card).length > 1;
        const list = it.priceCents || this.toCents(it.price);
        const third = i === 2;
        const right = third
          ? '<span class="pb-row__add">+' + this.money(plan.third && plan.third.targetCents ? plan.third.targetCents : list) + '</span><s>' + this.money(list) + '</s>'
          : '<span class="pb-row__incl">In the set</span><s>' + this.money(list) + '</s>';
        const links = third
          ? '<button type="button" data-pb-remove="' + esc(it.uid) + '">Remove</button>'
          : '<button type="button" data-pb-change="' + i + '">Change piece</button>';
        return '<div class="pb-row">' +
          '<span class="pb-row__img">' + (it.imageUrl ? '<img src="' + esc(it.imageUrl) + '" alt="">' : '') + '</span>' +
          '<div class="pb-row__info"><p class="pb-row__label">' + names[i] + '</p><p class="pb-row__title">' + esc(it.title) + '</p><p class="pb-row__variant">' + this.variantWithSize(it, sized) + '</p><p class="pb-row__links">' + links + '</p></div>' +
          '<div class="pb-row__price">' + right + '</div></div>';
      }).join('');
      let offer = '';
      if (this.thirdCents && this.items.length === 2 && !this.justAdded) {
        const tiles = this.thirdSuggestions().map(c => {
          const img = c.querySelector('product-slider img, img');
          const src = img ? (img.currentSrc || img.src || img.getAttribute('data-src') || '') : '';
          const list = parseInt(c.dataset.bogoPriceCents, 10) || 0;
          return '<button type="button" class="pb-third__tile" data-pb-third="' + c.dataset.bogoProductId + '">' +
            '<span class="pb-third__img">' + (src ? '<img src="' + esc(src) + '" alt="" loading="lazy">' : '') + '<span class="pb-third__plus" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16"><path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg></span></span>' +
            '<span class="pb-third__name">' + esc(c.dataset.bogoProductTitle) + '</span>' +
            '<span class="pb-third__price"><strong>' + this.money(this.thirdCents) + '</strong> <s>' + this.money(list) + '</s></span></button>';
        }).join('');
        offer = '<div class="pb-third"><p class="pb-third__title">Add a 3rd piece for only ' + this.money(this.thirdCents) + '</p><p class="pb-third__sub">One time offer only available with this set.</p><div class="pb-third__wrap"><div class="pb-third__row" data-pb-drag>' + tiles + '</div></div></div>';
      }
      box.innerHTML = '<div class="pb-nav"><button type="button" class="pb-back" data-pb-back>&larr; Back</button><button type="button" class="pb-reset" data-pb-reset>Start over</button></div><h2 class="pb-summary__title">Your set</h2>' + rows + offer;
    },

    /* 3rd-piece picks: a type the set does not have yet first, then the set's matched partners, then the rest */
    thirdSuggestions() {
      const inSet = this.items.map(i => i.productId);
      const types = this.items.map(i => { const c = this.grid.querySelector('[data-bogo-product-id="' + i.productId + '"]'); return c ? c.dataset.bogoType : ''; });
      const matches = this.items.map(i => { const c = this.grid.querySelector('[data-bogo-product-id="' + i.productId + '"]'); return c ? c.dataset.bogoMatch : ''; });
      const cards = Array.from(this.grid.querySelectorAll('[data-bogo-product-id]')).filter(c => {
        if (inSet.indexOf(c.dataset.bogoProductId) > -1) return false;
        /* No Perk necklaces in the $39 3rd-piece row (Jay, Oct 11 2026) */
        if (c.dataset.bogoType === 'Necklace') return false;
        if (c.querySelector('.bogo-select-btn[disabled]') && !this.cardSizeChips(c).some(b => this.sizeInStock(c, b.getAttribute('data-value')))) return false;
        const list = parseInt(c.dataset.bogoPriceCents, 10) || 0;
        return list > this.thirdCents && (list - this.thirdCents) / list <= this.MAX_DISCOUNT_RATE;
      });
      /* Within each type: matched partners of the set first, then best sellers (grid order is the collection's) */
      const rank = c => (matches.indexOf(c.dataset.bogoProductId) > -1 || inSet.indexOf(c.dataset.bogoMatch) > -1 ? 0 : 1);
      const byType = {};
      cards.forEach(c => { (byType[c.dataset.bogoType] = byType[c.dataset.bogoType] || []).push(c); });
      Object.keys(byType).forEach(t => byType[t].sort((a, b) => rank(a) - rank(b)));
      /* Take turns across types, starting with the one the set is missing, so the row is not all rings */
      const order = Object.keys(byType).sort((a, b) => (types.indexOf(a) === -1 ? 0 : 1) - (types.indexOf(b) === -1 ? 0 : 1));
      const out = [];
      for (let i = 0; out.length < 10 && order.some(t => byType[t][i]); i++) order.forEach(t => { if (byType[t][i] && out.length < 10) out.push(byType[t][i]); });
      return out;
    },

    /* Coming back after removing one piece of a pair from the bag: the piece left behind (its _pair_id has no partner)
       goes back in as the first pick, so the shopper only has to choose the second. Skipped once dismissed. */
    async resumeFromCart() {
      if (this.mode !== 'pair' || this.items.length) return;
      let cart;
      try { cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()); } catch (err) { return; }
      let dismissed = [];
      try { dismissed = JSON.parse(sessionStorage.getItem('bogo_pair_dismissed') || '[]'); } catch (err) { dismissed = []; }
      const count = {};
      cart.items.forEach(l => { const id = l.properties && l.properties._pair_id; if (id) count[id] = (count[id] || 0) + 1; });
      const orphans = cart.items.filter(l => { const id = l.properties && l.properties._pair_id; return id && count[id] === 1 && dismissed.indexOf(id) === -1; });
      if (!orphans.length || this.items.length) return;
      orphans.slice(0, this.maxItems).forEach(l => {
        if (!this.grid.querySelector('[data-bogo-product-id="' + l.product_id + '"]')) return;
        this.items.push({
          productId: String(l.product_id),
          variantId: String(l.variant_id),
          title: l.product_title,
          price: this.money(l.original_price),
          priceCents: l.original_price,
          compareAtPrice: '',
          imageUrl: l.image || '',
          handle: l.handle,
          uid: 'cart-' + l.properties._pair_id,
          inCartPairId: l.properties._pair_id
        });
      });
      this.saveState();
      this.render();
      if (this.items.length) this.setView('pick', { push: false });
    },

    /* Card galleries: the card shows two photos (product and model). Add the PDP's detail photos after them so the
       whole gallery swipes on this page. Photos follow the card's selected variant and load on the first swipe. */
    addCardGalleries() {
      this.grid.querySelectorAll('[data-bogo-product-id]').forEach(card => {
        const track = card.querySelector('product-slider .slider-slides');
        if (!track || track.querySelector('.bogo-extra-slide')) return;
        const have = new Set(Array.from(track.querySelectorAll('img')).map(i => (i.currentSrc || i.src || '').split('?')[0].replace(/_\d+x\d*(?=\.)/, '')));
        const bySrc = new Map();
        card.querySelectorAll('[data-detail-image]').forEach(w => {
          const img = w.querySelector('img');
          const src = img && (img.getAttribute('data-src') || img.getAttribute('src'));
          if (!src) return;
          let ids = [];
          try { ids = JSON.parse(w.dataset.variantIds || '[]').map(String); } catch (err) { ids = []; }
          const key = src.split('?')[0];
          if (have.has(key)) return;
          const prev = bySrc.get(key);
          if (prev) { prev.all = prev.all || !ids.length; ids.forEach(id => prev.ids.add(id)); }
          else bySrc.set(key, { src: src.replace(/width=\d+/, 'width=600'), alt: img.alt || '', ids: new Set(ids), all: !ids.length });
        });
        bySrc.forEach(d => {
          const slide = document.createElement('div');
          slide.className = 'slide product-card__image-wrapper fit-cover bogo-extra-slide';
          slide.setAttribute('data-variant-ids', JSON.stringify(Array.from(d.ids)));
          if (d.all) slide.setAttribute('data-all-variants', '');
          const img = document.createElement('img');
          img.setAttribute('data-src', d.src);
          img.alt = d.alt;
          img.decoding = 'async';
          slide.appendChild(img);
          track.appendChild(slide);
        });
        const ps = card.querySelector('product-slider');
        const load = () => card.querySelectorAll('.bogo-extra-slide img[data-src]').forEach(i => { i.src = i.getAttribute('data-src'); i.removeAttribute('data-src'); });
        if (ps) {
          ps.addEventListener('pointerdown', load, { once: true, passive: true });
          ps.addEventListener('touchstart', load, { once: true, passive: true });
        }
        this.syncCardGallery(card);
      });
    },

    /* Show the detail photos for the card's selected finish. Photos are saved on one variant per finish (often the
       20" or #9), so match on every option except size rather than on the exact variant. */
    syncCardGallery(card) {
      const extras = card.querySelectorAll('.bogo-extra-slide');
      if (!extras.length) return;
      const refEl = card.querySelector('[data-product-reference]');
      const vid = refEl ? String(refEl.dataset.selected || '') : '';
      let names = [];
      try { names = JSON.parse((refEl && refEl.dataset.options) || '[]'); } catch (err) { names = []; }
      const sizeIdx = names.findIndex(n => /size/i.test(n));
      const look = {};
      card.querySelectorAll('[data-variant-reference][data-options]').forEach(r => {
        try { look[r.getAttribute('data-id')] = JSON.parse(r.getAttribute('data-options') || '[]').filter((v, i) => i !== sizeIdx).join('|'); } catch (err) { /* skip */ }
      });
      const mine = look[vid];
      extras.forEach(sl => {
        let ids = [];
        try { ids = JSON.parse(sl.getAttribute('data-variant-ids') || '[]'); } catch (err) { ids = []; }
        const on = sl.hasAttribute('data-all-variants') || ids.indexOf(vid) > -1 || (mine != null && ids.some(id => look[id] === mine));
        sl.classList.toggle('is-selected', on);
      });
      const ps = card.querySelector('product-slider');
      if (ps && ps.carousel && ps.carousel.update) ps.carousel.update();
    },

    /* Preferred size for a list of size values: rings 9 then 10, chains 20", bracelets the largest. */
    preferredSizes(vals) {
      const norm = v => String(v).replace('#', '').replace('"', '').trim();
      const nums = vals.map(v => parseFloat(norm(v))).filter(n => !isNaN(n));
      if (vals.some(v => String(v).indexOf('"') > -1) && Math.max(...nums) >= 16) return ['20'];
      if (vals.some(v => String(v).indexOf('"') > -1)) return [String(Math.max(...nums))];
      return ['9', '10'];
    },

    /* Resolve one side of a ready-made pair to a cart item: chosen size, steel first, must be available. */
    pairSideItem(pairEl, side) {
      const el = pairEl.querySelector('[data-pair-side="' + side + '"]');
      if (!el) return null;
      let variants = [];
      try { variants = JSON.parse(el.getAttribute('data-variants') || '[]'); } catch (err) { return null; }
      const sel = el.querySelector('.bogo-pair__size');
      const size = sel ? sel.value : null;
      const norm = v => String(v == null ? '' : v).replace('#', '').replace('"', '').trim().toLowerCase();
      const isSteel = f => { const x = norm(f); return x.indexOf('gold') === -1 && x.indexOf('black') === -1; };
      /* The pair card shows steel, so it adds steel. Only a product with no steel at all falls back to its other finishes. */
      const base = variants.some(x => isSteel(x.finish)) ? variants.filter(x => isSteel(x.finish)) : variants;
      const v = base.filter(x => x.available && (!size || norm(x.size) === norm(size)))[0];
      if (!v) return null;
      return {
        productId: el.getAttribute('data-product-id'),
        variantId: String(v.id),
        title: el.getAttribute('data-title') || '',
        price: '$' + (v.price / 100).toFixed(0),
        priceCents: v.price,
        compareAtPrice: '',
        imageUrl: el.getAttribute('data-image') || '',
        handle: el.getAttribute('data-handle') || ''
      };
    },

    /* Start each pair card's size selects on the size most men buy, when that size is in stock. */
    defaultPairSizes() {
      document.querySelectorAll('.bogo-pair__side').forEach(el => {
        const sel = el.querySelector('.bogo-pair__size');
        if (!sel) return;
        let variants = [];
        try { variants = JSON.parse(el.getAttribute('data-variants') || '[]'); } catch (err) { return; }
        const norm = v => String(v == null ? '' : v).replace('#', '').replace('"', '').trim().toLowerCase();
        const vals = Array.from(sel.options).map(o => o.value);
        const inStock = {};
        const isSteel = f => { const x = norm(f); return x.indexOf('gold') === -1 && x.indexOf('black') === -1; };
        const base = variants.some(v => isSteel(v.finish)) ? variants.filter(v => isSteel(v.finish)) : variants;
        base.forEach(v => { if (v.available) inStock[norm(v.size)] = true; });
        Array.from(sel.options).forEach(o => { o.disabled = !inStock[norm(o.value)]; if (o.disabled && !/sold out/i.test(o.textContent)) o.textContent += ' - Sold out'; });
        const wants = this.preferredSizes(vals);
        for (const w of wants) {
          const opt = Array.from(sel.options).find(o => norm(o.value) === w.toLowerCase() && !o.disabled);
          if (opt) { sel.value = opt.value; return; }
        }
        const first = Array.from(sel.options).find(o => !o.disabled);
        if (first) sel.value = first.value;
      });
    },

    /* The card's own (hidden) size chips, which drive its selected variant. */
    /* Sizes smallest to largest (some products list 24" before 20", or 8.5" before 7.75"), on cards and in the quick view */
    sortSizeChips(root) {
      root.querySelectorAll('product-selector').forEach(ps => {
        if (!/size/i.test(ps.getAttribute('data-option') || '')) return;
        const chips = Array.from(ps.querySelectorAll('.selector__button[data-value]'));
        const num = b => parseFloat((b.getAttribute('data-value') || '').replace('#', ''));
        if (chips.length < 2 || chips.some(b => isNaN(num(b)))) return;
        /* Each chip sits in its own <li> in the theme's selector; sort whichever node is the list's child */
        const node = b => (b.parentElement.tagName === 'LI' ? b.parentElement : b);
        const parent = node(chips[0]).parentElement;
        if (!chips.every(b => node(b).parentElement === parent)) return;
        chips.slice().sort((a, b) => num(a) - num(b)).forEach(b => parent.appendChild(node(b)));
      });
    },

    cardSizeChips(card) {
      const sel = Array.from(card.querySelectorAll('product-selector')).find(ps => /size/i.test(ps.getAttribute('data-option') || ''));
      return sel ? Array.from(sel.querySelectorAll('.selector__button[data-value]')) : [];
    },

    /* Pair mode size picker: a bottom sheet like the PDP sticky bar's, so the card itself never changes size.
       Sizes mirror the card's hidden selector, in size order. A tap selects (with the PDP's tip on the selected
       size, e.g. "Most common size" on 9 and 10); Add to pair confirms. Rings get Find my size, the rest the size guide. */
    sizeKind(values) {
      const nums = values.map(v => parseFloat(String(v).replace('#', ''))).filter(n => !isNaN(n));
      if (!values.some(v => String(v).indexOf('"') > -1)) return 'ring';
      return Math.max(...nums) >= 14 ? 'chain' : 'bracelet';
    },

    sizeTips(card, kind) {
      const norm = x => String(x).toLowerCase().replace(/[^a-z0-9.\-]/g, '');
      const tips = {};
      const own = (card.dataset.bogoPopular || '').split(',').map(norm).filter(Boolean);
      if (own.length) { own.forEach(k => { tips[k] = 'Most common size'; }); return tips; }
      const map = kind === 'ring' ? { '9': 'Most common size', '10': 'Most common size' }
        : kind === 'bracelet' ? { '8.5': 'Most common size', '7.75': 'Best for smaller wrists', '7.5': 'Best for smaller wrists' }
        : { '18': 'Choker fit on most men', '20': 'Most common size', '24': 'Sits around mid chest' };
      Object.keys(map).forEach(k => { tips[norm(k)] = map[k]; });
      return tips;
    },

    /* Is this size in stock in the card's selected finish? The hidden size buttons carry no sold-out flag,
       so read the card's variant list (data-options + data-available) instead. */
    sizeInStock(card, value) {
      const refs = Array.from(card.querySelectorAll('[data-variant-reference][data-options]'));
      if (!refs.length) return true;
      const parse = el => { try { return JSON.parse(el.getAttribute('data-options') || '[]'); } catch (err) { return []; } };
      const refEl = card.querySelector('[data-product-reference]');
      let names = [];
      try { names = JSON.parse((refEl && refEl.dataset.options) || '[]'); } catch (err) { names = []; }
      const sizeIdx = names.findIndex(n => /size/i.test(n));
      /* The rest of the options (finish) come from the variant the card is on, which quick view keeps in sync too */
      const cur = refEl && refs.find(r => r.getAttribute('data-id') === String(refEl.dataset.selected));
      const curOpts = cur ? parse(cur) : null;
      return refs.some(r => {
        const o = parse(r);
        if (r.getAttribute('data-available') !== 'true') return false;
        if (sizeIdx > -1 ? o[sizeIdx] !== value : o.indexOf(value) === -1) return false;
        return !curOpts || o.every((v, i) => i === sizeIdx || v === curOpts[i]);
      });
    },

    openSizeSheet(card) {
      if (!this.sizeSheet) {
        const back = document.createElement('div');
        back.className = 'bogo-size-sheet__backdrop';
        const sheet = document.createElement('div');
        sheet.className = 'bogo-size-sheet';
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-label', 'Pick your size');
        sheet.innerHTML =
          '<div class="bogo-size-sheet__head">' +
            '<div class="bogo-size-sheet__img"></div>' +
            '<div class="bogo-size-sheet__info"><p class="bogo-size-sheet__title"></p><p class="bogo-size-sheet__label">Pick your size</p></div>' +
            '<button type="button" class="bogo-size-sheet__close" aria-label="Close">&times;</button>' +
          '</div>' +
          '<div class="bogo-size-sheet__chips"></div>' +
          '<button type="button" class="bogo-size-sheet__guide"></button>' +
          '<button type="button" class="bogo-size-sheet__add">Add to set</button>';
        document.body.appendChild(back);
        document.body.appendChild(sheet);
        back.addEventListener('click', () => this.closeSizeSheet());
        sheet.addEventListener('click', (e) => {
          if (e.target.closest('.bogo-size-sheet__close')) { this.closeSizeSheet(); return; }
          if (e.target.closest('.bogo-size-sheet__guide')) { this.openSizeHelp(); return; }
          if (e.target.closest('.bogo-size-sheet__add')) { this.confirmSizeSheet(); return; }
          const chip = e.target.closest('.bogo-size-chip');
          if (!chip || chip.classList.contains('is-unavailable')) return;
          this.markSheetSize(chip.getAttribute('data-value'));
        });
        this.sizeSheet = sheet;
        this.sizeSheetBack = back;
      }
      this.sizeSheetCard = card;
      const sheet = this.sizeSheet;
      const img = card.querySelector('img');
      sheet.querySelector('.bogo-size-sheet__img').innerHTML = img ? '<img src="' + (img.currentSrc || img.src) + '" alt="">' : '';
      sheet.querySelector('.bogo-size-sheet__title').textContent = card.dataset.bogoProductTitle || '';
      const reals = this.cardSizeChips(card).slice().sort((x, y) => parseFloat((x.getAttribute('data-value') || '').replace('#', '')) - parseFloat((y.getAttribute('data-value') || '').replace('#', '')));
      const values = reals.map(r => r.getAttribute('data-value') || '');
      const kind = this.sizeKind(values);
      this.sheetKind = kind;
      this.sheetTips = this.sizeTips(card, kind);
      const wrap = sheet.querySelector('.bogo-size-sheet__chips');
      wrap.innerHTML = '';
      /* One row of equal chips that fills the sheet (up to 7 a row): 2 bracelet sizes read as two big halves,
         7 ring sizes sit 6 to 12 on a single line instead of wrapping 5 + 2. */
      wrap.style.gridTemplateColumns = 'repeat(' + Math.max(1, Math.min(reals.length, 7)) + ', minmax(0, 1fr))';
      let current = null;
      reals.forEach(real => {
        const c = document.createElement('button');
        c.type = 'button';
        const off = real.classList.contains('is-unavailable') || real.disabled || !this.sizeInStock(card, real.getAttribute('data-value'));
        c.className = 'bogo-size-chip' + (off ? ' is-unavailable' : '');
        c.setAttribute('data-value', real.getAttribute('data-value'));
        c.textContent = real.getAttribute('data-value') || '';
        if (real.classList.contains('selected-value') && !off) current = real.getAttribute('data-value');
        wrap.appendChild(c);
      });
      /* Card sat on a sold-out size: start on the size most men buy that is in stock, else the first in stock */
      if (current == null) {
        const open = values.filter(v => this.sizeInStock(card, v));
        const norm = v => String(v).replace('#', '').replace('"', '').trim();
        current = this.preferredSizes(values).map(w => open.find(v => norm(v) === w)).find(Boolean) || open[0] || null;
      }
      /* Find my size for rings (the PDP's sizer sheet), the size chart for everything else */
      const guide = sheet.querySelector('.bogo-size-sheet__guide');
      const sizer = document.querySelector('[data-ring-sizer]');
      if (kind === 'ring' && window.thryftyRingSizer && sizer) {
        sizer.setAttribute('data-sizes', values.join(','));
        sizer.setAttribute('data-guide', card.dataset.bogoSizeGuide || '');
        sizer.setAttribute('data-soldout', values.filter(v => !this.sizeInStock(card, v)).join(','));
        guide.textContent = 'Find my size';
        guide.hidden = false;
      } else if (card.dataset.bogoSizeGuide) {
        guide.textContent = 'Size guide';
        guide.hidden = false;
      } else {
        guide.hidden = true;
      }
      this.markSheetSize(current);
      this.sizeSheetBack.classList.add('is-open');
      sheet.classList.add('is-open');
    },

    /* Select a size in the sheet and show its tip, the way the PDP does: only on the selected size. */
    markSheetSize(value) {
      const sheet = this.sizeSheet;
      if (!sheet) return;
      const norm = x => String(x).toLowerCase().replace(/[^a-z0-9.\-]/g, '');
      this.sheetValue = value;
      sheet.querySelectorAll('.bogo-size-chip').forEach(c => {
        const on = value != null && c.getAttribute('data-value') === value;
        c.classList.toggle('is-current', on);
        let tip = c.querySelector('.bogo-size-tip');
        const text = on ? this.sheetTips[norm(c.getAttribute('data-value'))] : null;
        if (text) {
          if (!tip) { tip = document.createElement('span'); tip.className = 'bogo-size-tip'; c.appendChild(tip); }
          tip.textContent = text;
          tip.style.left = ''; tip.style.right = ''; tip.style.transform = '';
          tip.classList.remove('is-start', 'is-end');
          /* Keep a long tip inside the sheet: pin it to the chip's left or right edge when centring would clip it */
          requestAnimationFrame(() => {
            const r = tip.getBoundingClientRect(), w = sheet.getBoundingClientRect();
            if (r.left < w.left + 8) { tip.style.left = '0'; tip.style.transform = 'none'; tip.classList.add('is-start'); }
            else if (r.right > w.right - 8) { tip.style.left = 'auto'; tip.style.right = '0'; tip.style.transform = 'none'; tip.classList.add('is-end'); }
          });
        } else if (tip) {
          tip.remove();
        }
      });
      const add = sheet.querySelector('.bogo-size-sheet__add');
      add.disabled = value == null;
      add.textContent = value == null ? 'Pick a size' : this.sheetEditUid ? 'Update size' : this.pendingThird ? 'Add for ' + this.money(this.thirdCents) : 'Add to set';
    },

    confirmSizeSheet() {
      const c = this.sizeSheetCard, value = this.sheetValue;
      if (!c || value == null || !this.sizeInStock(c, value)) return;
      this.sizeSheetConfirming = true;
      setTimeout(() => { this.sizeSheetConfirming = false; }, 0);
      const real = this.cardSizeChips(c).find(b => b.getAttribute('data-value') === value);
      if (real && !real.classList.contains('selected-value')) real.click();
      if (this.sheetEditUid) {
        /* Summary "Change size": update that one pick, keep its place in the set */
        const uid = this.sheetEditUid;
        this.sheetEditUid = null;
        this.closeSizeSheet();
        setTimeout(() => {
          const fresh = this.itemFromCard(c);
          const i = this.items.findIndex(x => x.uid === uid);
          if (fresh && i > -1) { fresh.uid = uid; this.items[i] = fresh; this.saveState(); this.render(); }
        }, 80);
        return;
      }
      this.closeSizeSheet();
      setTimeout(() => { if (this.canAddMore()) this.handleSelectToggle(c); }, 80);
    },

    /* Rings: the sizer opens on top and its "Select #9" lands back in the size sheet or quick view (see pdpv2-ring-sizer).
       Bracelets and chains: the theme's size guide modal for that product's guide. */
    openSizeHelp() {
      if (this.sizeSheetCard) this.openSizeHelpFor(this.sizeSheetCard);
    },

    openSizeHelpFor(card, guideType) {
      const values = this.cardSizeChips(card).map(r => r.getAttribute('data-value') || '');
      const sizer = document.querySelector('[data-ring-sizer]');
      if (values.length && this.sizeKind(values) === 'ring' && window.thryftyRingSizer && sizer) {
        sizer.setAttribute('data-sizes', values.join(','));
        sizer.setAttribute('data-guide', card.dataset.bogoSizeGuide || guideType || '');
        sizer.setAttribute('data-soldout', values.filter(v => !this.sizeInStock(card, v)).join(','));
        window.thryftyRingSizer.open();
        return;
      }
      const type = card.dataset.bogoSizeGuide || guideType;
      if (type) document.dispatchEvent(new CustomEvent('triggersizeguide', { detail: { type: type } }));
    },

    closeSizeSheet() {
      /* Dismissed without confirming: drop any pending size edit or 3rd-piece pick */
      if (this.sizeSheet && this.sizeSheet.classList.contains('is-open') && !this.sizeSheetConfirming) { this.sheetEditUid = null; this.pendingThird = false; this.renderPairBar && this.mode === 'pair' && this.renderPairBar(); }
      if (this.sizeSheet) this.sizeSheet.classList.remove('is-open');
      if (this.sizeSheetBack) this.sizeSheetBack.classList.remove('is-open');
      this.sizeSheetCard = null;
    },

    defaultCardSizes() {
      if (this.mode !== 'pair') return;
      const cards = this.grid.querySelectorAll('[data-bogo-product-id]');
      cards.forEach(card => {
        const sel = Array.from(card.querySelectorAll('product-selector')).find(ps => /size/i.test(ps.getAttribute('data-option') || ''));
        if (!sel) return;
        const chips = Array.from(sel.querySelectorAll('.selector__button[data-value]'));
        if (chips.length < 2) return;
        const vals = chips.map(b => b.getAttribute('data-value'));
        const norm = v => String(v).replace('#', '').replace('"', '').trim();
        const nums = vals.map(v => parseFloat(norm(v))).filter(n => !isNaN(n));
        let wants;
        if (vals.some(v => v.indexOf('"') > -1) && Math.max(...nums) >= 16) wants = ['20'];
        else if (vals.some(v => v.indexOf('"') > -1)) wants = [String(Math.max(...nums))];
        else wants = ['9', '10'];
        const current = chips.find(b => b.classList.contains('selected-value'));
        for (const w of wants) {
          const chip = chips.find(b => norm(b.getAttribute('data-value')) === w);
          if (!chip || chip === current) return;
          chip.click();
          if (this.isCurrentVariantAvailable(card)) return;
        }
        if (current) current.click();
      });
    },

    toCents(formatted, fallbackCents) {
      const n = parseFloat(String(formatted || '').replace(/[^0-9.]/g, ''));
      if (!isNaN(n) && n > 0) return Math.round(n * 100);
      const f = parseInt(fallbackCents, 10);
      return isNaN(f) ? 0 : f;
    },

    /* Split the picked items into pairs in pick order and price each pair at the set price.
       The cheaper piece is the anchor at full price; the pricier piece carries the target
       price (pair price minus the anchor), which keeps the discount as shallow as possible.
       Returns {pairs:[{anchor, discounted, targetCents, saveCents}], single} */
    pairPlan() {
      const items = this.items.filter(i => i.variantId);
      const plan = { pairs: [], single: null, listCents: 0, payCents: 0 };
      for (let i = 0; i + 1 < items.length; i += 2) {
        let a = items[i], b = items[i + 1];
        const ac = a.priceCents || this.toCents(a.price), bc = b.priceCents || this.toCents(b.price);
        let anchor = ac <= bc ? a : b, disc = ac <= bc ? b : a;
        const anchorCents = Math.min(ac, bc), discCents = Math.max(ac, bc);
        let target = this.pairCents - anchorCents;
        let ok = target > 0 && target < discCents && (discCents - target) / discCents <= this.MAX_DISCOUNT_RATE;
        plan.pairs.push({ anchor, discounted: disc, targetCents: ok ? target : null, listCents: ac + bc, payCents: ok ? this.pairCents : ac + bc });
        plan.listCents += ac + bc; plan.payCents += ok ? this.pairCents : ac + bc;
      }
      if (items.length % 2 === 1) plan.single = items[items.length - 1];
      /* Set flow: the 3rd pick rides on the pair at the 3rd-piece price */
      if (this.mode === 'pair' && this.thirdCents && items.length === 3 && plan.pairs.length === 1) {
        const t = items[2], tc = t.priceCents || this.toCents(t.price);
        const ok = tc > this.thirdCents && (tc - this.thirdCents) / tc <= this.MAX_DISCOUNT_RATE;
        plan.third = { item: t, targetCents: ok ? this.thirdCents : null };
        plan.single = null;
        plan.listCents += tc;
        plan.payCents += ok ? this.thirdCents : tc;
      }
      return plan;
    },

    money(cents) { return '$' + (cents / 100).toFixed(cents % 100 ? 2 : 0); },

    renderBar() {
      if (this.mode === 'pair') { this.renderPairBar(); if (this.view === 'summary') this.renderSummary(); return; }
      const count = this.items.length;
      const slots = this.bar.querySelectorAll('.bogo-bar__slot');
      const message = this.bar.querySelector('.bogo-bar__message-text');
      const subMessage = this.bar.querySelector('.bogo-bar__message-sub');
      const ctaBtn = this.bar.querySelector('.bogo-bar__cta-btn');
      const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
      const bonusSection = this.bar.querySelector('.bogo-bar__bonus');

      // Update slots
      slots.forEach((slot, i) => {
        const item = this.items[i];
        const imgEl = slot.querySelector('img');
        const removeBtn = slot.querySelector('.bogo-bar__slot-remove');

        if (item) {
          slot.classList.add('bogo-bar__slot--filled');
          slot.classList.remove('bogo-bar__slot--next');
          if (imgEl) {
            imgEl.src = item.imageUrl;
            imgEl.alt = item.title;
            imgEl.style.display = 'block';
          }
          if (removeBtn) {
            removeBtn.style.display = 'flex';
            removeBtn.dataset.bogoRemoveProductId = item.productId;
            removeBtn.dataset.bogoRemoveUid = item.uid || '';
          }
        } else {
          slot.classList.remove('bogo-bar__slot--filled');
          slot.classList.remove('bogo-bar__slot--next');
          if (imgEl) imgEl.style.display = 'none';
          if (removeBtn) removeBtn.style.display = 'none';

          // Highlight the next slot to fill
          if (i === count) {
            slot.classList.add('bogo-bar__slot--next');
          }
        }
      });

      // Show/hide bonus pair
      if (bonusSection) {
        if (count >= 2) {
          bonusSection.classList.add('bogo-bar__bonus--active');
        } else {
          bonusSection.classList.remove('bogo-bar__bonus--active');
        }
      }

      // Update message (BOGO mode only: pair mode returns early to renderPairBar)
      if (count === 0) {
        message.textContent = 'Select your first piece';
        subMessage.textContent = 'Pick 2 items for Buy One Get One';
      } else if (count === 1) {
        message.textContent = 'Add one more piece!';
        subMessage.textContent = '1 more to complete your BOGO';
      } else if (count === 2) {
        message.textContent = 'BOGO ready!';
        subMessage.textContent = 'Add 2 more for a bonus deal, or checkout now';
      } else if (count === 3) {
        message.textContent = 'Add one more for the bonus!';
        subMessage.textContent = '1 more to complete the bonus pair';
      } else if (count === 4) {
        message.textContent = 'Double BOGO complete!';
        subMessage.textContent = 'You\'re getting the best deal';
      }

      // Update CTA
      if (count === 0) {
        ctaBtn.disabled = true;
        ctaText.textContent = 'Select 2 more';
      } else if (count === 1) {
        ctaBtn.disabled = true;
        ctaText.textContent = 'Select 1 more';
      } else if (count === 2) {
        ctaBtn.disabled = false;
        ctaText.textContent = 'Add Pair to Bag';
      } else if (count === 3) {
        ctaBtn.disabled = false;
        ctaText.textContent = 'Select 1 more';
      } else {
        ctaBtn.disabled = false;
        ctaText.textContent = 'Add All to Bag';
      }
    },

    renderGrid() {
      const cards = this.grid.querySelectorAll('[data-bogo-product-id]');
      cards.forEach(card => {
        const productId = card.dataset.bogoProductId;
        const isSelected = this.isSelected(productId);
        const selectBtn = card.querySelector('.bogo-select-btn');

        card.classList.toggle('bogo-selected', isSelected);

        if (selectBtn) {
          // Check if current selected variant is available
          const currentVariantAvailable = this.isCurrentVariantAvailable(card);
          /* Pair mode picks the size in the sheet, so a card sitting on a sold-out size stays open as long as
             another size is in stock in this finish. */
          const sizes = this.mode === 'pair' ? this.cardSizeChips(card) : [];
          const anySizeOpen = sizes.length > 1 && sizes.some(b => this.sizeInStock(card, b.getAttribute('data-value')));

          if (selectBtn.dataset.bogoSoldOut === 'true') {
            // Entire product sold out - don't change
            return;
          } else if (isSelected && this.mode === 'pair') {
            /* Picked: blue, says so. Tapping it again adds a second copy (two chains), or, when this piece is
               the one being changed, keeps it and goes back to the set. */
            selectBtn.innerHTML = '<span aria-hidden="true" style="margin-right:6px">&#10003;</span>Selected';
            selectBtn.classList.add('bogo-selected-btn', 'pb-in-set');
            selectBtn.disabled = false;
          } else if (isSelected) {
            selectBtn.textContent = this.mode === 'pair' ? 'In your set' : 'Selected';
            selectBtn.classList.add('bogo-selected-btn');
            selectBtn.disabled = false;
          } else if (!currentVariantAvailable && !anySizeOpen) {
            selectBtn.textContent = 'Sold out';
            selectBtn.disabled = true;
            selectBtn.classList.remove('bogo-selected-btn');
          } else if (this.mode === 'pair' && !this.canAddMore()) {
            /* Set already has its two pieces: offer the 3rd piece at the set price, or just the way back to the set */
            selectBtn.textContent = this.thirdCents && this.items.length === 2 ? 'Add for ' + this.money(this.thirdCents) : 'View your set';
            selectBtn.disabled = false;
            selectBtn.classList.remove('bogo-selected-btn', 'pb-in-set');
          } else if (!this.canAddMore()) {
            selectBtn.textContent = 'Limit reached';
            selectBtn.disabled = true;
            selectBtn.classList.remove('bogo-selected-btn');
          } else {
            let label = this.mode === 'pair' ? 'Add to set' : 'Select for BOGO';
            selectBtn.textContent = label;
            selectBtn.disabled = false;
            selectBtn.classList.remove('bogo-selected-btn', 'pb-in-set');
          }
        }
      });
    },

    isCurrentVariantAvailable(card) {
      const productCard = card.querySelector('product-card');
      if (!productCard) return true;
      const refEl = productCard.querySelector('[data-product-reference]');
      if (!refEl) return true;
      const selectedId = refEl.dataset.selected;
      const variantEl = productCard.querySelector(`[data-variant-reference][data-id="${selectedId}"]`);
      if (!variantEl) return true;
      return variantEl.dataset.available === 'true';
    },

    // --- Event Binding ---

    bindEvents() {
      // Select/deselect from grid
      /* Ready-made pairs sit above the grid. Sizes are chosen on the pair card; one tap resolves each side to a
         variant (chosen size, steel finish first) and adds both at the pair price. */
      document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-add-pair]');
        if (!addBtn) return;
        e.preventDefault();
        const pairEl = addBtn.closest('.bogo-pair');
        const items = ['a', 'b'].map(side => this.pairSideItem(pairEl, side)).filter(Boolean);
        if (items.length !== 2) { addBtn.textContent = 'One size is sold out'; return; }
        /* Add the ready-made pair on its own, then put back any pieces already picked in the builder */
        const keep = this.items.slice();
        this.items = items;
        this.render();
        document.querySelectorAll('[data-add-pair]').forEach(b => b.classList.toggle('is-picked', b === addBtn));
        Promise.resolve(this.addToCart()).finally(() => {
          this.items = keep;
          this.saveState();
          this.render();
        });
      });
      document.querySelectorAll('.bogo-pair__size').forEach(sel => sel.addEventListener('click', e => e.stopPropagation()));
      this.defaultPairSizes();
      this.grid.addEventListener('click', (e) => {
        const selectBtn = e.target.closest('.bogo-select-btn');
        if (selectBtn) {
          e.preventDefault();
          e.stopPropagation();
          const card = selectBtn.closest('[data-bogo-product-id]');
          if (!card) return;
          /* Changing a piece and tapping the one already in that slot: its quick view offers Remove from set or Keep it */
          if (this.mode === 'pair' && this.replaceSlot != null && this.items[this.replaceSlot] && this.items[this.replaceSlot].productId === card.dataset.bogoProductId) {
            this.openQuickView(card);
            return;
          }
          /* Full set (two pieces, not changing one): a piece already in it goes to the set; any other piece is the
             3rd piece at the set price, or, with three in, the set */
          if (this.mode === 'pair' && this.replaceSlot == null && !this.pendingThird && this.items.length >= 2) {
            if (!this.isSelected(card.dataset.bogoProductId) && this.thirdCents && this.items.length === 2) { this.pendingThird = true; this.openQuickView(card); }
            else this.setView('summary');
            return;
          }
          /* Pair mode: Add to set opens the quick view (photos, details, finish, size, Find my size); its button adds */
          if (this.mode === 'pair' && this.canAddMore()) {
            this.openQuickView(card);
            return;
          }
          this.handleSelectToggle(card);
          return;
        }

        // Quick view trigger - clicking product image/link
        const qvBtn = e.target.closest('.bogo-qv-btn');
        if (qvBtn) {
          e.preventDefault();
          const qvCard = qvBtn.closest('[data-bogo-product-id]');
          if (qvCard) { this.primeThird(qvCard); this.openQuickView(qvCard); }
          return;
        }
        const productLink = e.target.closest('.product-link, product-slider a');
        if (productLink) {
          e.preventDefault();
          e.stopPropagation();
          const card = productLink.closest('[data-bogo-product-id]');
          if (card) { this.primeThird(card); this.openQuickView(card); }
          return;
        }
      });

      // Remove from bar slots
      this.bar.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.bogo-bar__slot-remove');
        if (removeBtn) {
          const uid = removeBtn.dataset.bogoRemoveUid;
          const productId = removeBtn.dataset.bogoRemoveProductId;
          if (uid) this.removeOne(uid);
          else if (productId) this.removeItem(productId);
          return;
        }

        const ctaBtn = e.target.closest('.bogo-bar__cta-btn');
        if (ctaBtn && !ctaBtn.disabled) {
          this.addToCart();
          return;
        }
      });

      // Quick view overlay close
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeQuickView());
      }
      if (this.quickView) {
        this.quickView.addEventListener('click', (e) => {
          if (e.target.closest('.bogo-qv__close')) {
            this.closeQuickView();
            return;
          }
          /* The size guide link in quick view is a copy of the card's, with no listener of its own */
          const sg = e.target.closest('[data-modal-trigger="size-guide"], .size-guide-trigger, .find-my-size-link');
          if (sg && this.qvSourceCard) {
            e.preventDefault();
            e.stopPropagation();
            this.openSizeHelpFor(this.qvSourceCard, sg.getAttribute('data-size-guide'));
            return;
          }
          const fam = e.target.closest('[data-pb-qvset-family]');
          if (fam && this.qvSet) {
            this.qvSet.family = fam.getAttribute('data-pb-qvset-family');
            this.qvSet.cards.forEach(c => { this.qvSet.picks[c.dataset.bogoProductId] = this.variantInFamily(c, this.qvSet.family); });
            this.renderSetQuickView(true);
            return;
          }
          const another = e.target.closest('.pb-qv-another');
          if (another && another.dataset.kind === 'keep') { this.closeQuickView(); this.replaceSlot = null; this.setView('summary'); return; }
          if (another) {
            const pid = this.quickView.dataset.bogoProductId;
            const card = pid && this.grid.querySelector('[data-bogo-product-id="' + pid + '"]');
            if (card && this.canAddMore()) { const it = this.itemFromCard(card); if (it && this.isCurrentVariantAvailable(card)) this.addItem(it); }
            this.closeQuickView();
            return;
          }
          const qvSelectBtn = e.target.closest('.bogo-qv__select-btn');
          if (qvSelectBtn && this.qvSet) { this.addSetFromQuickView(); return; }
          if (qvSelectBtn) {
            this.handleQuickViewSelect();
          }
        });
      }

      // Listen for variant changes within BOGO cards
      // The ProductCard web component dispatches 'optionvaluechange' internally,
      // which triggers optionChangeHandler → updates data-selected on the reference el.
      // We use a MutationObserver on data-selected to catch when the variant changes.
      this.grid.querySelectorAll('[data-product-reference]').forEach(refEl => {
        const observer = new MutationObserver(() => {
          const card = refEl.closest('[data-bogo-product-id]');
          if (!card) return;
          // Re-render grid to update sold-out state on select button
          this.renderGrid();
          this.syncCardGallery(card);
          // If this card is already selected, update the stored variant
          if (this.mode !== 'pair' && this.isSelected(card.dataset.bogoProductId)) {
            const variantData = this.getCardVariantData(card);
            if (variantData) {
              this.updateItemVariant(
                card.dataset.bogoProductId,
                variantData.variantId,
                variantData.price,
                variantData.compareAtPrice,
                variantData.imageUrl
              );
            }
          }
        });
        observer.observe(refEl, { attributes: true, attributeFilter: ['data-selected'] });
      });

      // ESC to close quick view
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.zoomOpen) { this.closeZoom(); return; }
        if (e.key === 'Escape' && !document.querySelector('[data-ring-sizer]:not([hidden])')) { this.closeQuickView(); this.closeSizeSheet(); }
      });
    },

    handleSelectToggle(card) {
      const productId = card.dataset.bogoProductId;

      if (this.mode === 'pair' && this.isSelected(productId) && !this.canAddMore()) {
        this.removeLastOf(productId);
      } else if (this.mode !== 'pair' && this.isSelected(productId)) {
        this.removeItem(productId);
      } else if (this.canAddMore()) {
        if (!this.isCurrentVariantAvailable(card)) {
          /* Sold out in this size and finish: let them pick a size that is in stock instead of adding a dead line */
          if (this.mode === 'pair') { if (!this.quickView || !this.quickView.classList.contains('bogo-qv-open')) this.openQuickView(card); }
          return;
        }
        const it = this.itemFromCard(card);
        if (it) this.addItem(it);
      }
    },

    itemFromCard(card) {
      const variantData = this.getCardVariantData(card);
      if (!variantData) return null;
      return {
        productId: card.dataset.bogoProductId,
        variantId: variantData.variantId,
        title: card.dataset.bogoProductTitle || '',
        price: variantData.price,
        priceCents: this.toCents(variantData.price, card.dataset.bogoPriceCents),
        compareAtPrice: variantData.compareAtPrice,
        imageUrl: variantData.imageUrl,
        handle: card.dataset.bogoProductHandle || ''
      };
    },

    getCardVariantData(card) {
      const refEl = card.querySelector('[data-product-reference]');
      if (!refEl) return null;

      let selectedVariantId = refEl.dataset.selected;
      // Fallback: if no variant selected, use the first variant reference
      if (!selectedVariantId) {
        const firstVariant = card.querySelector('[data-variant-reference][data-id]');
        if (firstVariant) selectedVariantId = firstVariant.dataset.id;
      }
      if (!selectedVariantId) return null;
      const variantRef = card.querySelector(`[data-variant-reference][data-id="${selectedVariantId}"]`);

      let price = card.dataset.bogoPrice || '0';
      let compareAtPrice = card.dataset.bogoComparePrice || '';
      let imageUrl = card.dataset.bogoImage || '';

      if (variantRef) {
        const priceMarkup = variantRef.querySelector('.variant-price-markup span');
        if (priceMarkup) price = priceMarkup.textContent.trim();
        const delMarkup = variantRef.querySelector('.variant-price-markup del');
        if (delMarkup) compareAtPrice = delMarkup.textContent.trim();
      }

      /* The picked finish's own photo, from the card's variant-tagged images. The card's visible slide can lag a
         variant set from the quick view or the finish pills, which showed a steel photo on an 18k gold pick (Oct 11 2026). */
      const byVariant = this.imageForVariant(card, selectedVariantId);
      const visibleImg = card.querySelector('.product-card__image-wrapper.is-selected img');
      if (byVariant) imageUrl = byVariant;
      else if (visibleImg && visibleImg.src) imageUrl = visibleImg.src;

      return {
        variantId: selectedVariantId,
        price: price,
        compareAtPrice: compareAtPrice,
        imageUrl: imageUrl
      };
    },

    // --- Quick View ---

    // Store the mapping of slide index → variant IDs for the current quick view
    qvSlideVariantMap: [],
    // Store the source card reference for the open quick view
    qvSourceCard: null,

    /**
     * Read all card image wrappers (featured + model + detail) into a structured array.
     * Each entry: { src, alt, variantIds: string[], type: 'featured'|'model'|'detail' }
     */
    /* First photo tagged with this variant (its finish), or '' */
    imageForVariant(card, variantId) {
      if (!card || !variantId) return '';
      try {
        const hit = this.readCardImages(card, card.dataset.bogoProductTitle).find(im => im.type === 'featured' && im.variantIds.indexOf(String(variantId)) > -1)
          || this.readCardImages(card, card.dataset.bogoProductTitle).find(im => im.variantIds.indexOf(String(variantId)) > -1);
        return hit ? hit.src : '';
      } catch (err) { return ''; }
    },

    readCardImages(card, title) {
      const allImages = [];

      // Helper to extract image data from a wrapper element
      const extract = (wrapper, type) => {
        const img = wrapper.querySelector('img');
        if (!img) return null;
        let src = img.src || img.dataset.src || '';
        src = src.replace(/width=\d+/, 'width=800');
        const alt = img.alt || title;
        let variantIds = [];
        try {
          variantIds = JSON.parse(wrapper.dataset.variantIds || '[]');
        } catch(e) {
          variantIds = [];
        }
        return { src, alt, variantIds: variantIds.map(String), type };
      };

      // Collect featured images
      card.querySelectorAll('[data-featured-image]').forEach(wrapper => {
        const data = extract(wrapper, 'featured');
        if (data) allImages.push(data);
      });

      // Collect model images
      card.querySelectorAll('[data-model-image]').forEach(wrapper => {
        const data = extract(wrapper, 'model');
        if (data) allImages.push(data);
      });

      // Collect detail images (from hidden bogo-detail-images container)
      card.querySelectorAll('[data-detail-image]').forEach(wrapper => {
        const data = extract(wrapper, 'detail');
        if (data) allImages.push(data);
      });

      return allImages;
    },

    /**
     * Build an ordered slide array for a given variant ID.
     * Order: variant featured → variant model → variant detail → other featured → other model → other detail
     * Detail images with empty variantIds (product-level) show for all variants.
     * Deduplicates by image URL to prevent the same photo appearing twice.
     */
    buildSlidesForVariant(allImages, variantId) {
      const vid = String(variantId);

      // Separate variant-matching vs non-matching, by type
      const matchFeatured = [];
      const matchModel = [];
      const matchDetail = [];
      const otherFeatured = [];
      const otherModel = [];
      const otherDetail = [];

      allImages.forEach(img => {
        // Detail images with empty variantIds are product-level (show for all variants)
        const matches = img.variantIds.includes(vid) ||
          (img.type === 'detail' && img.variantIds.length === 0);

        if (matches && img.type === 'featured') matchFeatured.push(img);
        else if (matches && img.type === 'model') matchModel.push(img);
        else if (matches && img.type === 'detail') matchDetail.push(img);
        else if (img.type === 'featured') otherFeatured.push(img);
        else if (img.type === 'model') otherModel.push(img);
        else otherDetail.push(img);
      });

      // Variant images first (featured → model → detail), then the rest
      let slides = [
        ...matchFeatured, ...matchModel, ...matchDetail,
        ...otherFeatured, ...otherModel, ...otherDetail
      ];

      /* Set flow: keep the gallery to the selected finish (plus photos shared by every finish) */
      const card = this.qvSourceCard;
      if (this.mode === 'pair' && card) {
        const refEl = card.querySelector('[data-product-reference]');
        let names = [];
        try { names = JSON.parse((refEl && refEl.dataset.options) || '[]'); } catch (err) { names = []; }
        const si = names.findIndex(n => /size/i.test(n));
        const finishOf = {};
        card.querySelectorAll('[data-variant-reference][data-options]').forEach(r => { try { finishOf[r.getAttribute('data-id')] = JSON.parse(r.getAttribute('data-options')).filter((v, k) => k !== si).join('|'); } catch (err) {} });
        const mine = finishOf[vid];
        if (mine != null) {
          const keep = slides.filter(sl => !sl.variantIds.length || sl.variantIds.some(id => finishOf[id] === mine));
          if (keep.length) slides = keep;
        }
      }

      // Deduplicate by src URL (same image can appear in multiple wrappers)
      const seen = new Set();
      slides = slides.filter(s => {
        const key = s.src.split('?')[0];
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return slides;
    },

    /**
     * Render the Swiper gallery with the given slides array and initialize it.
     */
    renderQvGallery(slides, start) {
      const qvImages = this.quickView.querySelector('.bogo-qv__images');
      if (!qvImages) return;

      // Destroy previous Swiper
      if (this.qvSwiper) {
        this.qvSwiper.destroy(true, true);
        this.qvSwiper = null;
      }

      // Build variant map for slideToVariant
      this.qvSlideVariantMap = slides.map(s => s.variantIds);
      this.qvSlides = slides;

      const slidesHtml = slides.map(slide =>
        `<div class="swiper-slide"><img src="${slide.src}" alt="${(slide.alt || '').replace(/"/g, '&quot;')}"></div>`
      ).join('');

      /* Set flow: the PDP gallery (peeked carousel, active photo centred, neighbours at both edges) with a thumbnail
         strip instead of dots (Jay, Oct 11 2026) */
      const pdpStyle = this.mode === 'pair';
      const thumbs = pdpStyle && slides.length > 1
        ? '<div class="pb-qv-thumbs">' + slides.map((sl, i) => '<button type="button" class="pb-qv-thumb' + (i === 0 ? ' is-active' : '') + '" data-pb-qv-thumb="' + i + '" aria-label="Image ' + (i + 1) + '"><img src="' + String(sl.src).replace(/([?&])width=\d+/, '$1width=160') + '" alt=""></button>').join('') + '</div>'
        : '';
      qvImages.innerHTML =
        '<div class="swiper bogo-qv__swiper' + (pdpStyle && slides.length < 2 ? ' is-single' : '') + '">' +
          '<div class="swiper-wrapper">' + slidesHtml + '</div>' +
          (slides.length > 1 && !pdpStyle ? '<div class="swiper-pagination bogo-qv__pagination"></div>' : '') +
          /* Full-screen viewer (Jay, Oct 11 2026): the corner icon says the photo opens bigger */
          (pdpStyle && slides.length ? '<button type="button" class="pb-qv-expand" data-pb-qv-expand aria-label="View photo full screen"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M12 3h5v5M8 17H3v-5M17 3l-5.5 5.5M3 17l5.5-5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' : '') +
        '</div>' + thumbs +
        /* PDP chevrons in the peek zones (same 16x26 glyph, colour and shadow as the product page gallery) */
        (thumbs ? ['prev', 'next'].map(d => '<button type="button" class="pb-qv-arrow pb-qv-arrow--' + d + '" data-pb-qv-arrow="' + d + '" aria-label="' + (d === 'prev' ? 'Previous image' : 'Next image') + '"><svg viewBox="0 0 16 26" aria-hidden="true"><path d="M3 2l10 11L3 24" fill="none" stroke="currentColor" stroke-width="4"/></svg></button>').join('') : '');
      qvImages.querySelectorAll('[data-pb-qv-arrow]').forEach(b => b.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.qvSwiper) return;
        if (b.getAttribute('data-pb-qv-arrow') === 'prev') this.qvSwiper.slidePrev(); else this.qvSwiper.slideNext();
      }));
      if (thumbs) {
        qvImages.querySelectorAll('[data-pb-qv-thumb]').forEach(b => b.addEventListener('click', () => {
          if (!this.qvSwiper) return;
          const i = parseInt(b.getAttribute('data-pb-qv-thumb'), 10);
          if (this.qvSwiper.params.loop) this.qvSwiper.slideToLoop(i); else this.qvSwiper.slideTo(i);
        }));
      }

      if (pdpStyle) {
        const expand = qvImages.querySelector('[data-pb-qv-expand]');
        if (expand) expand.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openZoom(this.qvSwiper ? this.qvSwiper.realIndex : 0);
        });
        /* One photo has no Swiper, so a plain tap opens it */
        if (slides.length < 2) {
          const img = qvImages.querySelector('.swiper-slide img');
          if (img) img.addEventListener('click', () => this.openZoom(0));
        }
      }

      if (slides.length > 1) {
        this.initQvSwiper(qvImages, 0, start || 0);
      }
    },

    /* Full-screen photo viewer over the quick view: swipe between photos, double-tap or pinch to zoom, close with the X,
       a swipe down, Esc or Back. Zoom lives only in here; the PDP's page zoom was pulled because it killed scrolling
       (Jay, Oct 11 2026) */
    openZoom(index) {
      const slides = this.qvSlides || [];
      if (!slides.length || this.zoomOpen || typeof Swiper === 'undefined') return;
      const root = this.zoomEl || this.buildZoom();
      const big = (src) => {
        src = String(src || '');
        return /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=1600') : src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=1600';
      };
      const esc = (v) => String(v || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      /* The quick view's photo (already loaded) shows at once; the 1600px file swaps in when it arrives */
      root.querySelector('.swiper-wrapper').innerHTML = slides.map(sl =>
        '<div class="swiper-slide"><div class="swiper-zoom-container"><img src="' + esc(sl.src) + '" data-pb-zoom-hi="' + esc(big(sl.src)) + '" alt="' + esc(sl.alt) + '" draggable="false"></div></div>'
      ).join('');
      root.querySelectorAll('img[data-pb-zoom-hi]').forEach(img => {
        const hi = new Image();
        hi.onload = () => { if (img.isConnected) img.src = hi.src; };
        hi.src = img.getAttribute('data-pb-zoom-hi');
      });
      root.classList.toggle('is-single', slides.length < 2);
      root.hidden = false;
      this.zoomOpen = true;
      this.zoomReturnFocus = document.activeElement;
      this.zoomResetDrag(false);
      const start = Math.max(0, Math.min(slides.length - 1, index || 0));
      this.zoomSwiper = new Swiper(root.querySelector('.pb-zoom__swiper'), {
        initialSlide: start,
        spaceBetween: 16,
        zoom: { maxRatio: 3, toggle: true },
        keyboard: { enabled: true, onlyInViewport: false },
        on: { slideChange: () => this.paintZoom() }
      });
      this.paintZoom();
      requestAnimationFrame(() => root.classList.add('is-open'));
      /* Back closes the viewer first, then the quick view on the next Back */
      try { history.pushState({ pb: this.view, pbZoom: 1 }, '', location.href); } catch (err) {}
      root.focus({ preventScroll: true });
    },

    buildZoom() {
      const root = document.createElement('div');
      root.className = 'pb-zoom';
      root.hidden = true;
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-label', 'Product photos');
      root.tabIndex = -1;
      const chevron = '<svg viewBox="0 0 16 26" aria-hidden="true"><path d="M3 2l10 11L3 24" fill="none" stroke="currentColor" stroke-width="4"/></svg>';
      root.innerHTML =
        '<div class="pb-zoom__bg"></div>' +
        '<div class="swiper pb-zoom__swiper"><div class="swiper-wrapper"></div></div>' +
        '<button type="button" class="pb-zoom__close" data-pb-zoom-close aria-label="Close full screen photos"><svg viewBox="0 0 15 15" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.49876 6.87911L14.2967 0L14.9996 0.71129L8.20166 7.5904L14.8222 14.29L14.1194 15.0013L7.49876 8.30169L0.878298 15.0012L0.1754 14.2899L6.79586 7.5904L-0.00195312 0.711417L0.700945 0.000126824L7.49876 6.87911Z" fill="currentColor"/></svg></button>' +
        '<button type="button" class="pb-zoom__arrow pb-zoom__arrow--prev" data-pb-zoom-arrow="prev" aria-label="Previous image">' + chevron + '</button>' +
        '<button type="button" class="pb-zoom__arrow pb-zoom__arrow--next" data-pb-zoom-arrow="next" aria-label="Next image">' + chevron + '</button>' +
        '<div class="pb-zoom__count" aria-live="polite"></div>';
      document.body.appendChild(root);
      this.zoomEl = root;

      root.querySelector('[data-pb-zoom-close]').addEventListener('click', () => this.closeZoom());
      root.querySelectorAll('[data-pb-zoom-arrow]').forEach(b => b.addEventListener('click', () => {
        if (!this.zoomSwiper) return;
        if (b.getAttribute('data-pb-zoom-arrow') === 'prev') this.zoomSwiper.slidePrev(); else this.zoomSwiper.slideNext();
      }));
      /* iOS: keep a pinch on the photo instead of zooming the whole page */
      root.addEventListener('gesturestart', (e) => e.preventDefault());

      /* Swipe down to close, only while the photo is not zoomed in */
      let sx = 0, sy = 0, dy = 0, track = false, pulling = false;
      root.addEventListener('touchstart', (e) => {
        track = e.touches.length === 1 && !!this.zoomSwiper && this.zoomSwiper.zoom.scale <= 1 && !e.target.closest('button');
        pulling = false; dy = 0;
        if (track) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }
      }, { passive: true });
      root.addEventListener('touchmove', (e) => {
        if (!track) return;
        if (e.touches.length !== 1) { track = false; this.zoomResetDrag(true); return; }
        const mx = e.touches[0].clientX - sx;
        const my = e.touches[0].clientY - sy;
        if (!pulling) {
          if (my > 12 && my > Math.abs(mx) * 1.3) pulling = true;
          else if (Math.abs(mx) > 12 || my < -12) { track = false; return; }
          else return;
        }
        dy = Math.max(0, my);
        this.zoomDrag(dy);
      }, { passive: true });
      const end = () => {
        if (!track) return;
        track = false;
        if (!pulling) return;
        if (dy > 110) this.closeZoom(); else this.zoomResetDrag(true);
      };
      root.addEventListener('touchend', end);
      root.addEventListener('touchcancel', end);
      return root;
    },

    zoomDrag(dy) {
      const root = this.zoomEl;
      const slide = root && root.querySelector('.swiper-slide-active .swiper-zoom-container');
      if (slide) { slide.style.transition = 'none'; slide.style.transform = 'translateY(' + dy + 'px) scale(' + (1 - Math.min(dy / 2000, 0.12)) + ')'; }
      const bg = root && root.querySelector('.pb-zoom__bg');
      if (bg) { bg.style.transition = 'none'; bg.style.opacity = String(1 - Math.min(dy / 360, 0.75)); }
    },

    zoomResetDrag(animate) {
      const root = this.zoomEl;
      if (!root) return;
      root.querySelectorAll('.swiper-zoom-container').forEach(el => { el.style.transition = animate ? 'transform 0.2s ease' : 'none'; el.style.transform = ''; });
      const bg = root.querySelector('.pb-zoom__bg');
      if (bg) { bg.style.transition = animate ? 'opacity 0.2s ease' : 'none'; bg.style.opacity = ''; }
    },

    paintZoom() {
      const root = this.zoomEl;
      const sw = this.zoomSwiper;
      if (!root || !sw) return;
      const n = sw.slides.length;
      const count = root.querySelector('.pb-zoom__count');
      if (count) count.textContent = n > 1 ? (sw.activeIndex + 1) + ' / ' + n : '';
      const prev = root.querySelector('[data-pb-zoom-arrow="prev"]');
      const next = root.querySelector('[data-pb-zoom-arrow="next"]');
      if (prev) prev.hidden = n < 2 || sw.isBeginning;
      if (next) next.hidden = n < 2 || sw.isEnd;
    },

    closeZoom(fromPop) {
      if (!this.zoomOpen) return;
      this.zoomOpen = false;
      const root = this.zoomEl;
      const i = this.zoomSwiper ? this.zoomSwiper.activeIndex : 0;
      if (this.zoomSwiper) { this.zoomSwiper.destroy(true, true); this.zoomSwiper = null; }
      if (root) { root.classList.remove('is-open'); root.hidden = true; root.querySelector('.swiper-wrapper').innerHTML = ''; }
      /* Land the quick view on the photo you were just looking at */
      /* Rebuilt at that photo: slideToLoop is unreliable in this looped peek carousel and lands on the wrong one */
      if (this.qvSwiper && this.qvSlides && this.qvSlides.length > 1) this.renderQvGallery(this.qvSlides, i);
      if (!fromPop && history.state && history.state.pbZoom) {
        this.zoomPopSkip = true;
        history.back();
      }
      const back = this.zoomReturnFocus;
      this.zoomReturnFocus = null;
      if (back && document.contains(back) && back.focus) back.focus({ preventScroll: true });
    },

    paintQvThumbs(i) {
      const strip = this.quickView && this.quickView.querySelector('.pb-qv-thumbs');
      if (!strip) return;
      strip.querySelectorAll('.pb-qv-thumb').forEach((b, k) => b.classList.toggle('is-active', k === i));
      const on = strip.querySelector('.pb-qv-thumb.is-active');
      if (on) strip.scrollTo({ left: Math.max(0, on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2), behavior: 'smooth' });
    },

    openQuickView(card) {
      if (!this.quickView || !this.overlay) return;

      const productId = card.dataset.bogoProductId;
      const title = card.dataset.bogoProductTitle || '';
      const handle = card.dataset.bogoProductHandle || '';

      this.qvSourceCard = card;

      // Populate quick view
      const qvTitle = this.quickView.querySelector('.bogo-qv__title');
      const qvPrice = this.quickView.querySelector('.bogo-qv__price');
      const qvImages = this.quickView.querySelector('.bogo-qv__images');
      const qvSelectors = this.quickView.querySelector('.bogo-qv__selectors');
      const qvSelectBtn = this.quickView.querySelector('.bogo-qv__select-btn');
      const qvDescription = this.quickView.querySelector('.bogo-qv__description');

      if (qvTitle) qvTitle.textContent = title;

      // Clone price from card (set flow: what this piece costs inside the set, list price struck)
      const cardPrice = card.querySelector('[data-list-price]');
      if (qvPrice && this.mode === 'pair') {
        this.paintQvSetPrice(card);
      } else if (qvPrice && cardPrice) {
        qvPrice.innerHTML = cardPrice.innerHTML;
      }

      // Build image gallery from the card's featured + model image wrappers
      if (qvImages) {
        // Read all images from card DOM (featured + model, each with variant IDs)
        this.qvAllImages = this.readCardImages(card, title);

        // Get the currently selected variant ID
        const refEl = card.querySelector('[data-product-reference]');
        const currentVariantId = refEl ? refEl.dataset.selected : '';

        let slides = [];

        if (this.qvAllImages.length > 0) {
          // Build slides ordered for the current variant
          slides = this.buildSlidesForVariant(this.qvAllImages, currentVariantId);
        }

        // Fallback: use data-bogo-images if no card images found
        if (slides.length === 0) {
          let images = [];
          try {
            const raw = card.getAttribute('data-bogo-images');
            if (raw) images = JSON.parse(raw);
          } catch(e) {
            images = [];
          }
          if (!images || images.length === 0) {
            const fallbackImg = card.dataset.bogoImage;
            if (fallbackImg) {
              images = [{ src: fallbackImg, alt: title }];
            }
          }
          slides = images.map(img => ({ src: img.src, alt: img.alt || title, variantIds: [], type: 'featured' }));
          this.qvAllImages = slides;
        }

        this.renderQvGallery(slides);
      }

      // Clone selectors from card and wire up variant-image switching
      if (qvSelectors) {
        const cardSelectors = card.querySelector('.selectors');
        if (cardSelectors) {
          qvSelectors.innerHTML = '<div class="selectors">' + cardSelectors.innerHTML + '</div>';
          // Wire up click handlers on cloned selector buttons
          this.bindQvSelectorEvents(card, qvSelectors);
          this.sortSizeChips(qvSelectors);
          const vals = this.cardSizeChips(card).map(r => r.getAttribute('data-value') || '');
          if (vals.length && this.sizeKind(vals) === 'ring') qvSelectors.querySelectorAll('.size-guide-trigger, .find-my-size-link').forEach(el => {
            const t = el.querySelector('span:not(.visually-hidden)') || el;
            if (t.firstChild && t.firstChild.nodeType === 3) t.firstChild.nodeValue = 'Find my size';
          });
        } else {
          qvSelectors.innerHTML = '';
        }
      }

      // Description from data attribute
      if (qvDescription) {
        const desc = card.dataset.bogoProductDescription || '';
        qvDescription.innerHTML = desc;
        qvDescription.style.display = desc ? 'block' : 'none';
      }

      // Update select button
      this.quickView.dataset.bogoProductId = productId;
      if (qvSelectBtn && this.pairQvInSet(productId)) {
        this.paintPairQvButton(productId);
      } else if (qvSelectBtn) {
        this.togglePairQvAnother(false);
        if (this.mode === 'pair' && this.canAddMore()) {
          qvSelectBtn.textContent = this.pendingThird ? 'Add for ' + this.money(this.thirdCents) : 'Add to set';
          qvSelectBtn.classList.remove('bogo-qv__remove-btn');
          qvSelectBtn.disabled = false;
        } else if (this.isSelected(productId)) {
          qvSelectBtn.textContent = this.mode === 'pair' ? 'Remove from set' : 'Remove from BOGO';
          qvSelectBtn.classList.add('bogo-qv__remove-btn');
          qvSelectBtn.disabled = false;
        } else if (this.canAddMore()) {
          qvSelectBtn.textContent = this.mode === 'pair' ? 'Add to set' : 'Select for BOGO';
          qvSelectBtn.classList.remove('bogo-qv__remove-btn');
          qvSelectBtn.disabled = false;
        } else {
          /* Set flow: three pieces in, so this one cannot join; the button goes to the set instead of a dead end */
          qvSelectBtn.textContent = this.mode === 'pair' ? 'View your set' : 'BOGO slots full';
          qvSelectBtn.classList.remove('bogo-qv__remove-btn');
          qvSelectBtn.disabled = this.mode !== 'pair';
        }
      }

      // Open
      /* Every quick view opens at the top: the scroll box kept the last product's position (Jay, Oct 11 2026) */
      const qvScroll = this.quickView.querySelector('.bogo-qv__scroll');
      if (qvScroll) qvScroll.scrollTop = 0;
      this.overlay.classList.add('bogo-qv-open');
      this.quickView.classList.add('bogo-qv-open');
      document.body.style.overflow = 'hidden';
    },

    // All card images for the current quick view (persisted so swatch clicks can reorder)
    qvAllImages: [],

    /**
     * Bind click events on quick view selectors to:
     * 1. Update selected state visually in the QV
     * 2. Trigger the variant change on the source card (so data-selected updates)
     * 3. Rebuild the gallery ordered for the new variant (featured first, then model)
     */
    bindQvSelectorEvents(card, qvSelectors) {
      const refEl = card.querySelector('[data-product-reference]');
      if (!refEl) return;

      const options = JSON.parse(refEl.dataset.options || '[]');
      const variantEls = card.querySelectorAll('[data-variant-reference]');

      // Build variants array from card data
      const variants = [];
      variantEls.forEach(vEl => {
        variants.push({
          id: vEl.dataset.id,
          available: vEl.dataset.available === 'true',
          options: JSON.parse(vEl.dataset.options || '[]')
        });
      });

      // Track selected options (read current from card)
      const selectedOptions = {};
      const currentVariant = variants.find(v => v.id === refEl.dataset.selected);
      if (currentVariant) {
        options.forEach((optName, idx) => {
          selectedOptions[optName] = currentVariant.options[idx];
        });
      }

      this.paintQvStock(card, qvSelectors);
      /* One live handler per quick view. The selectors box is reused, so handlers from earlier quick views used to pile
         up and change other cards' variants and repaint stock from the wrong product (QA, Oct 11 2026). */
      if (this._qvSelHandler) qvSelectors.removeEventListener('click', this._qvSelHandler);
      /* Sold-out sizes can't be picked: stop the theme's selector from highlighting them (capture runs first) */
      if (!this._qvSoldOutGuard) {
        this._qvSoldOutGuard = (e) => {
          const b = e.target.closest('.selector__button');
          if (b && b.classList.contains('bogo-unavailable')) { e.preventDefault(); e.stopPropagation(); }
        };
        qvSelectors.addEventListener('click', this._qvSoldOutGuard, true);
      }
      // Listen for clicks on selector buttons in the quick view
      this._qvSelHandler = (e) => {
        const btn = e.target.closest('.selector__button');
        if (!btn || btn.classList.contains('bogo-unavailable')) return;

        const selectorEl = btn.closest('[data-option]') || btn.closest('product-selector');
        if (!selectorEl) return;

        const optName = selectorEl.dataset.option || selectorEl.getAttribute('data-option');
        const value = btn.dataset.value;
        if (!optName || !value) return;

        // Update selected option
        selectedOptions[optName] = value;

        // Update visual selected state in QV selectors
        const siblingBtns = selectorEl.querySelectorAll('.selector__button');
        siblingBtns.forEach(b => b.classList.remove('selected-value'));
        btn.classList.add('selected-value');

        // Find matching variant
        const matchedVariant = variants.find(v => {
          return options.every((opt, idx) => v.options[idx] === selectedOptions[opt]);
        });

        if (matchedVariant) {
          // Update the source card's data-selected so BOGO state stays in sync
          refEl.setAttribute('data-selected', matchedVariant.id);
          this.paintQvStock(card, qvSelectors);

          // Update QV price
          const qvPrice = this.quickView.querySelector('.bogo-qv__price');
          const variantRef = card.querySelector(`[data-variant-reference][data-id="${matchedVariant.id}"]`);
          if (this.mode === 'pair') {
            /* Set flow: keep "Included in the set" (or the 3rd-piece price) when the finish or size changes */
            this.paintQvSetPrice(card, matchedVariant.id);
          } else if (qvPrice && variantRef) {
            const priceMarkup = variantRef.querySelector('.variant-price-markup');
            if (priceMarkup) qvPrice.innerHTML = priceMarkup.innerHTML;
          }

          // Rebuild the gallery ordered for this variant
          // (variant's featured + model first, then the rest)
          const newSlides = this.buildSlidesForVariant(this.qvAllImages, matchedVariant.id);
          this.renderQvGallery(newSlides);
        }
      };
      qvSelectors.addEventListener('click', this._qvSelHandler);
    },

    /* Quick view price in the set flow: "Included in the set" with the list price struck, or the 3rd-piece price.
       Uses the chosen variant's own price when it has one (finishes can differ). */
    paintQvSetPrice(card, variantId) {
      const qvPrice = this.quickView && this.quickView.querySelector('.bogo-qv__price');
      if (!qvPrice) return;
      let cents = parseInt(card.dataset.bogoPriceCents, 10) || 0;
      const ref = variantId && card.querySelector('[data-variant-reference][data-id="' + variantId + '"] .variant-price-markup');
      if (ref) { const c = this.toCents(ref.textContent); if (c) cents = c; }
      const list = this.money(cents);
      qvPrice.innerHTML = this.pendingThird
        ? '<strong class="pb-qv-price">' + this.money(this.thirdCents) + '</strong> <s>' + list + '</s>'
        : '<span class="pb-qv-incl">Included in the set</span> <s>' + list + '</s>';
    },

    /* Quick view: grey out sizes sold out in the chosen finish, and never offer Add to pair on a sold-out variant */
    paintQvStock(card, qvSelectors) {
      const sizeSel = Array.from(qvSelectors.querySelectorAll('[data-option]')).find(el => /size/i.test(el.getAttribute('data-option') || ''));
      if (sizeSel) sizeSel.querySelectorAll('.selector__button[data-value]').forEach(b => {
        const off = !this.sizeInStock(card, b.getAttribute('data-value'));
        b.classList.toggle('bogo-unavailable', off);
        b.classList.toggle('is-unavailable', off);
      });
      const btn = this.quickView.querySelector('.bogo-qv__select-btn');
      if (!btn || (this.isSelected(card.dataset.bogoProductId) && this.mode !== 'pair')) return;
      if (this.pairQvInSet(card.dataset.bogoProductId)) { this.paintPairQvButton(card.dataset.bogoProductId); return; }
      const ok = this.isCurrentVariantAvailable(card);
      if (!ok) { btn.textContent = 'Sold out in this size'; btn.disabled = true; }
      else if (this.canAddMore()) { btn.textContent = this.mode === 'pair' ? (this.pendingThird ? 'Add for ' + this.money(this.thirdCents) : 'Add to set') : 'Select for BOGO'; btn.disabled = false; }
    },

    initQvSwiper(qvImages, attempt, initialSlide) {
      attempt = attempt || 0;
      initialSlide = initialSlide || 0;
      // Wait for Swiper global to be available (CDN defer)
      if (typeof Swiper === 'undefined') {
        if (attempt < 20) {
          setTimeout(() => this.initQvSwiper(qvImages, attempt + 1, initialSlide), 100);
        }
        return;
      }
      // Wait a frame for the modal to have layout dimensions
      setTimeout(() => {
        const swiperEl = qvImages.querySelector('.bogo-qv__swiper');
        if (!swiperEl) return;
        this.qvSwiper = this.mode === 'pair'
          ? new Swiper(swiperEl, {
              /* Like the PDP: looped, so the first photo also has a neighbour peeking on its left */
              slidesPerView: 'auto',
              centeredSlides: true,
              spaceBetween: 6,
              loop: swiperEl.querySelectorAll('.swiper-slide').length >= 3,
              initialSlide: initialSlide,
              on: {
                slideChange: (sw) => this.paintQvThumbs(sw.realIndex),
                click: (sw, e) => {
                  const t = e && e.target;
                  if (!t || !t.closest || t.closest('[data-pb-qv-expand]')) return;
                  const slide = t.closest('.swiper-slide');
                  if (!slide) return;
                  const at = slide.hasAttribute('data-swiper-slide-index')
                    ? parseInt(slide.getAttribute('data-swiper-slide-index'), 10)
                    : sw.slides.indexOf(slide);
                  if (slide.classList.contains('swiper-slide-active')) this.openZoom(sw.realIndex);
                  else if (sw.params.loop) sw.slideToLoop(at); else sw.slideTo(at);
                }
              }
            })
          : new Swiper(swiperEl, {
              slidesPerView: 1,
              spaceBetween: 0,
              loop: false,
              initialSlide: initialSlide,
              pagination: {
                el: qvImages.querySelector('.bogo-qv__pagination'),
                clickable: true,
                type: 'bullets'
              }
            });
        if (this.mode === 'pair') this.paintQvThumbs(initialSlide);
      }, 50);
    },

    closeQuickView() {
      if (this.zoomOpen) this.closeZoom();
      if (this.overlay) this.overlay.classList.remove('bogo-qv-open');
      if (this.quickView) this.quickView.classList.remove('bogo-qv-open');
      document.body.style.overflow = '';
      // Clean up Swiper
      if (this.qvSwiper) {
        this.qvSwiper.destroy(true, true);
        this.qvSwiper = null;
      }
      this.qvSlideVariantMap = [];
      this.qvAllImages = [];
      this.qvSourceCard = null;
      this.pendingThird = false;
      this.qvSet = null;
    },

    /* A piece already in the set, opened from the grid (not as the 3rd-piece offer) */
    pairQvInSet(productId) {
      return this.mode === 'pair' && !this.pendingThird && this.isSelected(productId);
    },

    paintPairQvButton(productId) {
      const btn = this.quickView && this.quickView.querySelector('.bogo-qv__select-btn');
      if (!btn) return;
      btn.textContent = 'Remove from set';
      btn.classList.add('bogo-qv__remove-btn');
      btn.disabled = false;
      /* Changing this very piece: Keep it goes back to the set. Otherwise duplicates are allowed, so offer a second copy
         while there is an open slot. */
      const changingThis = this.replaceSlot != null && this.items[this.replaceSlot] && this.items[this.replaceSlot].productId === productId;
      this.togglePairQvAnother(changingThis || (this.replaceSlot == null && this.canAddMore()), changingThis ? 'keep' : 'another');
    },

    togglePairQvAnother(show, kind) {
      if (!this.quickView) return;
      let link = this.quickView.querySelector('.pb-qv-another');
      if (!link && show) {
        const btn = this.quickView.querySelector('.bogo-qv__select-btn');
        if (!btn) return;
        link = document.createElement('button');
        link.type = 'button';
        link.className = 'pb-qv-another';
        link.textContent = 'Add another one';
        btn.insertAdjacentElement('afterend', link);
      }
      if (link) {
        link.hidden = !show;
        link.dataset.kind = kind || 'another';
        link.textContent = kind === 'keep' ? 'Keep it in my set' : 'Add another one';
      }
    },

    handleQuickViewSelect() {
      const productId = this.quickView.dataset.bogoProductId;
      if (!productId) return;
      /* Set flow: a piece already in the set offers Remove from set (Jay, Oct 11 2026). When changing a slot, that slot goes. */
      if (this.pairQvInSet(productId)) {
        const slot = this.replaceSlot != null && this.items[this.replaceSlot] && this.items[this.replaceSlot].productId === productId ? this.replaceSlot : -1;
        if (slot > -1) { this.items.splice(slot, 1); this.saveState(); } else { this.removeLastOf(productId); }
        this.replaceSlot = null;
        this.closeQuickView();
        this.setView('pick', { scroll: false });
        return;
      }

      if (this.mode === 'pair' && !this.isSelected(productId) && !this.canAddMore()) {
        this.closeQuickView();
        this.setView('summary');
        return;
      }

      if (this.isSelected(productId) && !(this.mode === 'pair' && this.canAddMore())) {
        this.removeLastOf(productId);
        this.closeQuickView();
      } else {
        const card = this.grid.querySelector(`[data-bogo-product-id="${productId}"]`);
        if (card) {
          this.handleSelectToggle(card);
          this.closeQuickView();
        }
      }
    },

    // --- Cart Integration ---

    async addToCart() {
      if (this.isLoading || !this.canCheckout()) return;
      this.isLoading = true;

      const ctaBtn = this.bar.querySelector('.bogo-bar__cta-btn');
      ctaBtn.classList.add('bogo-loading');
      ctaBtn.disabled = true;

      // Filter to only items with valid variant IDs
      const validItems = this.items.filter(item => item.variantId && !isNaN(parseInt(item.variantId)));
      if (validItems.length === 0) {
        console.error('BOGO: No valid variant IDs found', this.items);
        const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
        if (ctaText) ctaText.textContent = 'Try again';
        this.isLoading = false;
        ctaBtn.classList.remove('bogo-loading');
        return;
      }

      let items = validItems.map(item => ({
        id: parseInt(item.variantId),
        quantity: 1
      }));
      if (this.mode === 'pair') {
        /* Pair pricing travels on the pricier line as the two properties the bundle discount
           function verifies: the target price in cents and the anchor product that justifies it.
           Attach first, anchor second, so the anchor sits above it at checkout. */
        const plan = this.pairPlan();
        items = [];
        /* Both lines of a pair share _pair_id: the drawer shows them as one "Any 2 for $99" item, the lines never merge
           with a loose copy of the same piece, and a piece left behind when its partner is removed can be found again. */
        const stamp = Date.now().toString(36);
        plan.pairs.forEach((pr, n) => {
          const pairId = stamp + '-' + n;
          if (n === 0 && plan.third && plan.third.targetCents && pr.targetCents) {
            /* 3rd piece: discounted on the same set anchor as the pair partner */
            items.push({ id: parseInt(plan.third.item.variantId), quantity: 1, properties: { _bundle_price: String(plan.third.targetCents), _bundle_anchor: 'gid://shopify/Product/' + pr.anchor.productId, _pair_offer: 'Any 2 for ' + this.money(this.pairCents), _pair_id: pairId, _set_piece: '3' } });
          } else if (n === 0 && plan.third) {
            items.push({ id: parseInt(plan.third.item.variantId), quantity: 1 });
          }
          if (pr.targetCents) {
            /* _set_piece: the pick order (1st, 2nd, 3rd) so the cart lists the set the way the summary did */
            const pieceOf = it => String(Math.max(1, this.items.indexOf(it) + 1));
            items.push({ id: parseInt(pr.discounted.variantId), quantity: 1, properties: { _bundle_price: String(pr.targetCents), _bundle_anchor: 'gid://shopify/Product/' + pr.anchor.productId, _pair_offer: 'Any 2 for ' + this.money(this.pairCents), _pair_id: pairId, _set_piece: pieceOf(pr.discounted) } });
            const anchorProps = { _pair_id: pairId, _pair_offer: 'Any 2 for ' + this.money(this.pairCents), _set_piece: pieceOf(pr.anchor) };
            if (this.thirdCents && !plan.third) anchorProps._set_more = String(this.thirdCents);
            items.push({ id: parseInt(pr.anchor.variantId), quantity: 1, properties: anchorProps });
          } else {
            items.push({ id: parseInt(pr.discounted.variantId), quantity: 1 });
            items.push({ id: parseInt(pr.anchor.variantId), quantity: 1 });
          }
        });
        if (plan.single) items.push({ id: parseInt(plan.single.variantId), quantity: 1 });
      }

      if (this.mode === 'pair' && this.items.some(i => i.inCartPairId)) {
        try {
          const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
          /* The set being rebuilt (a leftover piece, or a set reopened from the cart for a 3rd piece) is replaced whole */
          const ids = this.items.map(i => i.inCartPairId).filter(Boolean);
          const updates = cart.items.map(l => (l.properties && ids.indexOf(l.properties._pair_id) > -1) ? 0 : l.quantity);
          await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: updates }) });
        } catch (err) { /* worst case the leftover stays at full price beside the new pair */ }
      }

      /* Ask Shopify for the drawer's fresh HTML in the same call, the way the PDP attach does. Without it the
         drawer opens showing the cart as it was at page load, which reads as "nothing was added". */
      const cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items: items };
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        body.sections = cartEl.getSectionsToRender().map(s => s.id);
        body.sections_url = window.location.pathname;
      }
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          console.error('Shopify cart error:', errorBody);
          throw new Error(errorBody.description || 'Cart error');
        }

        const data = await response.json();

        // Publish cart update event for cart drawer/icon
        if (typeof publish === 'function') {
          publish('cart-update', {
            source: 'bogo-builder',
            cartData: data
          });
        }

        // Notify ReBuy mini-cart to refresh
        document.dispatchEvent(new CustomEvent('rebuy:cart:change'));

        // Clear BOGO selections (pair mode: cleared from storage now, kept on screen as "Added" until the drawer closes)
        const added = this.items;
        this.items = [];
        this.saveState();
        if (this.mode === 'pair') { this.items = added; this.justAdded = true; this.renderPairBar(); }
        else this.render();
        /* Pair mode: the set stays on screen behind the cart drawer; the builder resets to the landing only once the
           drawer closes, so adding never feels like being sent to another page */
        if (this.mode === 'pair') {
          const drawer = document.querySelector('cart-drawer');
          const reset = () => { this.justAdded = false; this.items = []; this.saveState(); history.replaceState(null, '', location.pathname + location.search); this.setView('landing', { push: false, scroll: true }); };
          if (drawer && window.MutationObserver) {
            let opened = false;
            const mo = new MutationObserver(() => {
              if (drawer.classList.contains('active')) opened = true;
              else if (opened) { mo.disconnect(); reset(); }
            });
            mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
            setTimeout(() => { if (!opened) { mo.disconnect(); reset(); } }, 4000);
          } else {
            reset();
          }
        }

        // Update cart count in nav
        this.updateCartCount();

        // Show success then redirect to cart or open drawer
        const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
        if (ctaText) ctaText.textContent = 'Added to cart \u2713';

        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data && data.sections) {
            // Fresh drawer HTML came back with the add: render it (this also opens the drawer)
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(data);
          } else if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            const cartLink = document.querySelector('#cart-icon-bubble');
            if (cartLink) cartLink.click();
          }
        }, 400);

      } catch (err) {
        console.error('BOGO add to cart failed:', err);
        const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
        if (ctaText) ctaText.textContent = 'Try again';
        /* Say why (Shopify's reason, e.g. a size that just sold out) under the total for a few seconds (QA, Oct 11 2026) */
        const nudge = this.bar && this.bar.querySelector('[data-pb-nudge]');
        if (nudge && this.mode === 'pair') {
          const why = err && err.message && err.message !== 'Cart error' ? err.message : 'Your set couldn\u2019t be added. Try again.';
          nudge.textContent = why; nudge.hidden = false; nudge.classList.add('is-error');
          this.cartErrorUntil = Date.now() + 7000;
          setTimeout(() => { this.cartErrorUntil = 0; nudge.classList.remove('is-error'); this.render(); }, 7000);
        }
      } finally {
        this.isLoading = false;
        ctaBtn.classList.remove('bogo-loading');
        /* Pair mode keeps the added set on screen behind the drawer; the reset on drawer close redraws it */
        setTimeout(() => this.render(), 2000);
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const countBubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden]');
        countBubbles.forEach(el => {
          el.textContent = cart.item_count;
        });
      } catch(e) { /* silent */ }
    }
  };

  // Initialize when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => BOGO.init());
  } else {
    BOGO.init();
  }

  // Expose for debugging
  window.BOGO = BOGO;
})();
